// Control UI startup English. Lazy fragments retain empty namespace anchors here
// so host-side composition preserves grouped translation-memory source order.
import type { TranslationMap } from "../lib/types.ts";
import * as agentEn from "./en-agents.ts";

export const en: TranslationMap & {
  mcpApp: TranslationMap;
  linkReader: TranslationMap;
  agentTools: TranslationMap;
  githubPublication: TranslationMap & Record<"newAction" | "capacity", string>;
  githubConnections: TranslationMap &
    Record<"title" | "mine" | "system" | "forMe" | "forSystem", string>;
  board: TranslationMap & { widget: TranslationMap };
  browser: TranslationMap & { errors: TranslationMap; annotatePrompt: TranslationMap };
  shortcutsOverlay: TranslationMap & { title: string };
  chat: TranslationMap & {
    codeBlock: TranslationMap;
    commands: TranslationMap;
    detailPanel: TranslationMap;
    welcome: TranslationMap & { suggestions: TranslationMap & { whatCanYouDo: string } };
    goals: TranslationMap;
    messages: TranslationMap &
      Record<
        | "copySelection"
        | "replyingTo"
        | "message"
        | "showLess"
        | "showMore"
        | "tooLargeToDisplay"
        | "unattributedSender"
        | "toolSender"
        | "errorSender",
        string
      >;
    pullRequests: TranslationMap & Record<"open" | "draft" | "merged" | "closed", string>;
    processesPanel: TranslationMap;
  };
  configForm: TranslationMap & { sections: TranslationMap };
  configPage: TranslationMap;
  profilePage: TranslationMap & {
    personalInstructions: TranslationMap;
    identity: TranslationMap &
      Record<
        | "title"
        | "menuLabel"
        | "menuButtonLabel"
        | "description"
        | "avatar"
        | "chooseAvatar"
        | "displayName"
        | "linkedEmails",
        string
      >;
  };
  connection: TranslationMap;
  configView: TranslationMap;
  custodian: TranslationMap;
  debug: TranslationMap & {
    lanes: TranslationMap & Record<"lane" | "active" | "queued" | "blocked", string>;
    overlay: TranslationMap &
      Record<
        | "title"
        | "eyebrow"
        | "move"
        | "minimize"
        | "expand"
        | "lanes"
        | "status"
        | "activeRuns"
        | "events"
        | "cpu"
        | "memory"
        | "delayP99",
        string
      >;
  };
  // Lazy en-devices.ts assigns into this namespace.
  devices: TranslationMap & { pairing: TranslationMap };
  desktop: TranslationMap &
    Record<"title" | "openWindow" | "unavailable" | "toggle" | "reconnect" | "connecting", string>;
  sessionHovercard: TranslationMap & { states: TranslationMap; checks: TranslationMap } & Record<
      | "ariaLabel"
      | "linkedChannel"
      | "moreParticipants"
      | "moreParticipantsLabel"
      | "projectLabel"
      | "workspaceLabel"
      | "branchLabel"
      | "runsOn"
      | "machineLabel"
      | "machineCpu"
      | "machineMemory"
      | "more"
      | "changedFile"
      | "changedFiles",
      string
    >;
  filePreview: TranslationMap;
  updates: TranslationMap;
  login: TranslationMap;
  labsPage: TranslationMap;
  mcpServers: TranslationMap;
  mcpPage: TranslationMap;
  modelSetup: TranslationMap;
  newSession: TranslationMap &
    Record<"title" | "hint" | "placementReloadBlocked" | "discardUnsavedAndReload", string>;
  commandPalette: TranslationMap;
  palette: TranslationMap & {
    placeholder: string;
    categories: TranslationMap & { navigation: string };
  };
  sessionsView: TranslationMap;
  skillWorkshop: TranslationMap;
  systems: TranslationMap;
  talkPage: TranslationMap;
  usage: TranslationMap & { overview: TranslationMap };
} = {
  pluginUi: {
    sessionRecentMessages: "Recent messages",
    sessionHistoryUnavailable: "Session history could not be loaded. Try again.",
    sessionHistoryEmpty: "No recent messages.",
    customize: "Customize UI",
    selectionScope: "Choose views for this browser window. Built-in views are always available.",
    builtin: "Built-in view",
    retryView: "Retry plugin view",
    reload: "Reload plugin UI",
    dashboardTitle: "Session dashboard",
    dashboardEmpty: "This session has no dashboard widgets yet.",
    customPluginsDisabled: "Custom plugin UI is off",
    customPluginsEnableHint: "Enable Custom plugin UI in Labs.",
    openLabs: "Open Labs",
    surface: {
      "session-list": "Session list",
      composer: "Composer",
      workspace: "Workspace",
      transcript: "Transcript",
      "tool-result": "Tool results",
    },
  },
  capacityMeter: {
    workerSlots: "{used} of {total} slots busy",
    unavailable: "Slot utilization unavailable",
    execHost: "Codex exec",
  },
  agentsHome: {},
  common: {
    uploadsDisabled: "File and image uploads are disabled.",
    health: "Health",
    ok: "OK",
    yes: "Yes",
    no: "No",
    default: "Default",
    active: "Active",
    loading: "Loading…",
    gatewayStarting: "Gateway starting…",
    refreshing: "Refreshing…",
    offline: "Offline",
    connect: "Connect",
    connected: "Connected",
    refresh: "Refresh",
    retry: "Retry",
    staleData: "Showing stale data.",
    reload: "Reload",
    reset: "Reset",
    restore: "Restore",
    unarchive: "Unarchive",
    undo: "Undo",
    probe: "Check connection",
    call: "Call",
    confirm: "Confirm",
    dontAskAgain: "Don't ask me again",
    cancel: "Cancel",
    close: "Close",
    previous: "Previous",
    next: "Next",
    continue: "Continue",
    back: "Back",
    create: "Create",
    copy: "Copy",
    copied: "Copied!",
    copyFailed: "Copy failed",
    copyCode: "Copy code",
    copyTable: "Copy table",
    expandTable: "Expand table",
    expandedTable: "Expanded table",
    closeTable: "Close expanded table",
    resizeSplitView: "Resize split view",
    delete: "Delete",
    remove: "Remove",
    dismiss: "Dismiss",
    dismissAndDontShowAgain: "Dismiss and don't show again",
    unselect: "Unselect",
    enabled: "Enabled",
    disabled: "Disabled",
    failed: "Failed",
    none: "none",
    na: "n/a",
    never: "never",
    justNow: "just now",
    now: "now",
    unknown: "Unknown",
    configured: "Configured",
    running: "Running",
    queued: "Queued",
    completed: "Completed",
    cancelled: "Cancelled",
    linked: "Linked",
    mode: "Mode",
    system: "System",
    light: "Light",
    dark: "Dark",
    baseUrl: "Base URL",
    lastStart: "Last start",
    lastProbe: "Last connection check",
    lastConnect: "Last connect",
    lastMessage: "Last message",
    authAge: "Auth age",
    credential: "Credential",
    audience: "Audience",
    publicKey: "Public Key",
    probeOk: "Connection check passed",
    probeFailed: "Connection check failed",
    reloadConfig: "Reload Config",
    multiSelect: {
      addCustom: "Add “{value}”",
      noMatches: "No matches",
      remove: "Remove {value}",
    },
    loadConfig: "Load config",
    loadApprovals: "Load approvals",
    settingsSections: "Settings sections",
    version: "Version",
    docs: "Docs",
    learnMore: "Learn more",
    colorMode: "Color mode",
    colorModeOption: "Color mode: {mode}",
    search: "Search",
    pickerNoMatches: "No matches",
    save: "Save",
    saving: "Saving…",
    saveAndPublish: "Save & Publish",
    importing: "Importing…",
    importFromRelays: "Import from Relays",
    showAdvanced: "Show Advanced",
    hideAdvanced: "Hide Advanced",
    unsavedChanges: "You have unsaved changes",
    working: "Working…",
    showQr: "Show QR",
    relink: "Relink",
    waitForScan: "Wait for scan",
    logout: "Logout",
    skipToMainContent: "Skip to main content",
  },
  focus: {
    unsupported: "This focused view is not supported.",
  },
  shortcutsOverlay: { title: "Keyboard shortcuts" },
  optionCard: {
    recommended: "Recommended",
    skip: "Skip for now",
  },
  nativeConversation: {
    openDashboardFailed: "Couldn't open that page in the Dashboard",
  },
  nativeLinkMenu: {
    label: "Link actions",
    openInline: "Open in Browser Panel",
    openExternal: "Open in Default Browser",
    copy: "Copy Link",
  },
  browserLinkPreferences: {
    openInControlUi: "Open links in Control UI browser",
  },
  filePreview: {
    bundle: {},
    label: "Support files",
  },
  mcpApp: {},
  sessionHovercard: {
    ariaLabel: "Session information",
    linkedChannel: "Linked to {channel}",
    moreParticipants: "+{count}",
    moreParticipantsLabel: "{count} more participants",
    projectLabel: "Project",
    workspaceLabel: "Workspace",
    branchLabel: "Branch",
    runsOn: "Runs on {providerId} · {profileId}",
    machineLabel: "Machine",
    machineCpu: "{cpu} vCPU",
    machineMemory: "{memory} GB",
    more: "+{count} more",
    changedFile: "{count} file",
    changedFiles: "{count} files",
    states: {},
    checks: {},
  },
  sessionProgressCard: {
    title: "Progress",
    composerTitle: "Task progress",
    shortCount: "{completed} of {total}",
    noteLabel: "Progress note",
    dismiss: "Dismiss progress card",
    clearSaved: "Clear saved progress for everyone",
    clearFailed: "Could not clear saved progress card.",
    refresh: {
      label: "Refresh task progress",
      retry: "Retry progress refresh",
      pending: "Refreshing task progress…",
      failed: "Could not refresh. Previous update kept.",
      timeout: "No new update yet. The request may still be running.",
      updated: "Task progress updated",
    },
    widgetLabel: "Session progress",
    widgetLoading: "Loading session progress…",
    widgetEmpty: "No progress card yet",
    widgetUnavailable: "Session progress is unavailable.",
    widgetAccessDenied: "Select a session you can access or change sharing for this session.",
    countLabel: "{completed} of {total} completed",
    activity: {
      updated: "Updated {time}",
      completed: "Completed {time}",
      failed: "Failed {time}",
      stopped: "Stopped {time}",
    },
    outcome: {
      completed: "Completed",
      failed: "Failed",
      stopped: "Stopped",
    },
    stepLabel: "{step}, {status}",
    status: {
      completed: "completed",
      failed: "failed",
      inProgress: "in progress",
      paused: "paused",
      pending: "pending",
      stopped: "stopped",
    },
    receipt: {
      updating: "Updating progress",
      failed: "Progress update failed",
      skipped: "Progress update skipped",
      updated: "Progress updated — {completed}/{total} · {current}",
      noteUpdated: "Progress note updated",
      cleared: "Progress cleared",
    },
  },
  channels: {
    lastError: "Last error",
    refreshingStaleSnapshot:
      "Refreshing channel status in the background; showing the last successful snapshot.",
    hub: {
      connectedTitle: "Connected channels",
      addTitle: "Add a channel",
      addSubtitle: "Pick a service and follow the guided setup.",
      updatedAgo: "Updated {ago}",
      noneConnected: "No channels connected yet. Pick one below to get started.",
      partialSnapshot: "Some channel checks did not finish before the UI budget.",
      stateRunning: "Running",
      stateConfigured: "Configured",
      stateAttention: "Needs attention",
      setUp: "Set up",
      guidedSetup: "Guided step-by-step setup",
      openDetails: "Open details",
      lastMessageAgo: "Last message {ago}",
      browseAllTitle: "More channels…",
      browseAllSubtitle: "Browse every available channel, including installable plugins.",
      runSetup: "Run setup",
      adminRequired: "Browsing only. Channel setup requires operator.admin access.",
      saveBeforeSetup:
        "You have unsaved channel config changes. Save or reload them before running guided setup.",
    },
    pairing: {
      title: "DM access requests",
      subtitle: "Review people waiting to send direct messages to pairing-protected channels.",
      channelFilter: "Channel",
      accountFilter: "Account",
      allChannels: "All channels",
      allAccounts: "All accounts",
      approve: "Approve",
      dismiss: "Dismiss",
      approveAria: "Approve {sender} for {channel}, account {account}",
      dismissAria: "Dismiss {sender} for {channel}, account {account}",
      requested: "Requested {ago}",
      expires: "Expires {ago}",
      senderDetails: "Sender details",
      missingPermission:
        "This connection does not have operator.pairing access, so DM requests cannot be reviewed.",
      noAccounts: "No configured channel accounts use DM sender pairing.",
      noRequests: "No pending DM access requests.",
      noFilteredRequests: "No pending requests match these filters.",
      limits:
        "Requests expire after {minutes} minutes. Each channel account can hold up to {count} pending requests.",
      detailTitle: "DM access",
      detailSubtitle: "People must be approved before their direct messages reach the agent.",
      pendingCount: "{count} pending",
      noPending: "No pending requests",
      review: "Review requests",
      approveDialogTitle: "Approve DM access",
      dismissDialogTitle: "Dismiss DM access request",
      approveExplanation:
        "This lets the sender talk to the agent in direct messages. It does not grant group access.",
      dismissExplanation:
        "This removes the current request but does not block the sender. They can request access again later.",
      notifyRequester: "Notify the requester after approval",
      makeCommandOwner: "Also make this sender the first command owner",
      commandOwnerHelp:
        "Command owners can run privileged commands and approve dangerous actions. This option is only available while no owner is configured.",
      commandOwnerNeedsAdmin:
        "No command owner is configured. This connection needs operator.admin to assign the first owner.",
      approvedNotice: "DM access approved.",
      approvedOwnerNotice: "DM access approved and the first command owner was configured.",
      approvedNotificationFailedNotice:
        "DM access approved, but the requester notification could not be delivered.",
      approvedOwnerFailedNotice:
        "DM access approved, but the first command owner could not be configured.",
      approvedFollowupsFailedNotice:
        "DM access approved, but requester notification and command-owner setup both failed.",
      dismissedNotice: "DM access request dismissed. The sender can request access again.",
    },
    setup: {
      dialogLabel: "Set up {channel}",
      title: "Set up {channel}",
      genericTitle: "a channel",
      subtitle: "Guided channel setup",
      starting: "Starting setup…",
      working: "Working…",
      sessionExpired:
        "This setup session expired after the Gateway restarted. Close this dialog, then start channel setup again.",
      continue: "Continue",
      finish: "Finish",
      copyText: "Copy",
      openLink: "Open link",
      viewDocs: "View docs",
      doneTitle: "Channel configured",
      doneBody:
        "Configuration saved. The gateway reloads the channel automatically; check its card for live status.",
      doneNoChangesTitle: "No changes made",
      doneNoChangesBody: "Setup finished without configuring a channel. Nothing was saved.",
      whatsappScanTitle: "Link WhatsApp by scanning the QR code",
      whatsappScanHelp:
        "On your phone, open WhatsApp → Settings → Linked devices → Link a device, then scan this code.",
      whatsappQrLoading: "Generating QR code…",
      whatsappQrHint: "Click Show QR to generate a pairing code.",
      whatsappQrAlt: "WhatsApp QR",
      whatsappLinked: "WhatsApp is linked and ready.",
      regenerateQr: "New QR code",
      linkLater: "Link later",
    },
    config: {
      schemaUnavailable: "Schema unavailable. Use Raw.",
      channelSchemaUnavailable: "Channel config schema unavailable.",
      loadingSchema: "Loading config schema…",
    },
    generic: {
      subtitle: "Channel status and configuration.",
    },
    discord: {
      title: "Discord",
      subtitle: "Bot status and channel configuration.",
    },
    googleChat: {
      title: "Google Chat",
      subtitle: "Chat API webhook status and channel configuration.",
    },
    imessage: {
      title: "iMessage",
      subtitle: "macOS bridge status and channel configuration.",
    },
    signal: {
      title: "Signal",
      subtitle: "signal-cli status and channel configuration.",
    },
    slack: {
      title: "Slack",
      subtitle: "Socket mode status and channel configuration.",
    },
    telegram: {
      title: "Telegram",
      subtitle: "Bot status and channel configuration.",
    },
    whatsapp: {
      title: "WhatsApp",
      subtitle: "Link WhatsApp Web and monitor connection health.",
      phoneNumber: "Phone number",
      loggedOut: "Logged out.",
      logoutConfirmTitle: "Log out of WhatsApp account {accountId}?",
      logoutConfirmMessage:
        "Logging out of account {accountId} stops its listener and deletes its saved credentials.",
      logoutNotCleared:
        "No stored WhatsApp session was cleared. It may already be absent, or its auth directory may require manual cleanup.",
    },
    nostr: {
      title: "Nostr",
      subtitle: "Decentralized DMs via Nostr relays (NIP-04).",
      profile: "Profile",
      editProfile: "Edit Profile",
      profilePicture: "Profile picture",
      noProfile: "No profile set.",
      noProfileHint: 'Click "Edit Profile" to add your name, bio, and avatar.',
      name: "Name",
      displayName: "Display Name",
      about: "About",
      advanced: "Advanced",
      profilePicturePreview: "Profile picture preview",
      account: "Account",
      username: "Username",
      usernameHelp: "Short username (e.g., satoshi)",
      bio: "Bio",
      bioPlaceholder: "Tell people about yourself...",
      bioHelp: "A brief bio or description",
      displayNameHelp: "Your full display name",
      avatarUrl: "Avatar URL",
      avatarHelp: "HTTPS URL to your profile picture",
      bannerUrl: "Banner URL",
      bannerHelp: "HTTPS URL to a banner image",
      website: "Website",
      websiteHelp: "Your personal website",
      nip05Identifier: "NIP-05 Identifier",
      nip05Help: "Verifiable identifier (e.g., you@domain.com)",
      lightningAddress: "Lightning Address",
      lightningHelp: "Lightning address for tips (LUD-16)",
      placeholders: {
        username: "satoshi",
        displayName: "Satoshi Nakamoto",
        avatarUrl: "https://example.com/avatar.jpg",
        bannerUrl: "https://example.com/banner.jpg",
        website: "https://example.com",
        nip05: "you@example.com",
        lightningAddress: "you@getalby.com",
      },
      notices: {
        timeout:
          "Request timed out after 30 seconds; the server may still have applied the change — check the profile before retrying.",
        operationFailed: "{prefix}: {error}",
        updateFailed: "Profile update failed",
        publishFailed: "Profile publish failed on all relays.",
        published: "Profile published to relays.",
        importFailed: "Profile import failed",
        importedFromRelays: "Profile imported from relays. Review and publish.",
        imported: "Profile imported. Review and publish.",
      },
    },
  },
  agentStartup: {
    title: "Starting up",
    description: "The agent is getting ready. This view will load automatically.",
    short: "Starting up…",
  },
  lazyView: {
    errorTitle: "Panel failed to load",
    genericSubtitle: "Something went wrong while loading this panel.",
    staleTitle: "This view could not load",
    staleSubtitle:
      "Some interface files could not be downloaded. Check your connection, then reload.",
    reloading: "Reloading…",
    retry: "Retry",
    stylesFailed: "Styles failed to load, so the page may look broken.",
  },
  communityInvite: {
    title: "Pull up a chair.",
    body: "There’s a place for you in the AI future. Join the Community on Reddit, Discord, and X.",
    join: "Join",
    follow: "Follow",
    joinPlatform: "Join the OpenClaw community on {platform}",
    followPlatform: "Follow OpenClaw on {platform}",
    dismissForever: "Dismiss and don't show again",
    dismissFailed: "Invitation dismissed, but your preference couldn't be saved.",
  },
  updates: {
    adminRequired: "Administrator access is required to change update settings or start an update.",
    campaign: {
      countdown: "Updating in {time}",
      waitingForIdle: "Waiting for active work · forced update in {time}",
      held: "Update held · resumes in {time}",
      applying: "Applying update…",
    },
    holdOneHour: "Hold 1 h",
    reviewUpdate: "Review update",
    run: {
      title: "Update progress",
      phases: "Update phases",
      steps: "Installation steps",
      details: "Step details",
      noDetails: "No step details recorded yet.",
      verification: "Verification checks",
      report: "Update report",
      restarting: "Gateway restarting…",
      reconnecting: "Reconnecting automatically. Update progress is saved on the gateway.",
      progress: "{completed} of {total} phases complete",
      phase: {
        requested: "Requested",
        staging: "Staging",
        validating: "Validating",
        repairing: "Repairing",
        activating: "Activating",
        restarting: "Restarting",
        verifying: "Verifying",
        finished: "Finished",
      },
      step: {
        completed: "Complete",
        in_progress: "In progress",
        pending: "Pending",
        failed: "Failed",
        skipped: "Skipped",
      },
      oracle: {
        service: "Service",
        version: "Version",
        plugins: "Plugins",
        channels: "Channels",
        inference: "Inference",
      },
      oracleState: {
        pass: "Passed",
        warn: "Not verified",
        fail: "Failed",
        pending: "Pending",
      },
    },
    confirm: {
      action: "Update and restart",
    },
    target: {
      version: "v{version}",
      commitBehind: "{count} commit behind",
      commitsBehind: "{count} commits behind",
      viewChanges: "Compare on GitHub",
    },
    sidebar: {
      campaignTarget: "{status} · {target}",
      checking: "Checking for updates…",
      checkFailed: "Could not check the latest update. Try again.",
      updating: "Updating Gateway…",
      availableTitle: "Update available",
      availableSummary: "A newer revision is ready.",
      blockedTitle: "Update blocked",
      blockedSummary: "The selected revision could not be applied to this checkout.",
      blockedWaiting: "Blocked · waiting for active work · {target}",
      waitingForIdle: "Waiting for active work",
    },
    dialog: {
      disconnected:
        "The Gateway disconnected during the update. This page reconnects on its own. If it stays disconnected, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair.",
      restarting:
        "The Gateway is restarting. This page disconnects and reconnects on its own. If it stays disconnected, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair.",
    },
    channel: {},
    installKind: {},
    page: {},
    coalescedRestart:
      "Update installed. A gateway restart is already in progress; status will refresh after it reconnects.",
    error: "Update error: {error}",
    checkError: "Could not check for updates: {error}",
    status: "Update {status}: {reason}. {guidance}",
    verificationFailedWithIdentity:
      "Update finished, but the running install does not match the expected revision. Expected {expected}, running {actual}. Run `openclaw triage` on the Gateway host before retrying.",
    handoffTimeout:
      "Update completion was not confirmed. Run `openclaw triage` on the Gateway host and inspect the result before retrying.",
    outcomeUnknown:
      "The update outcome is unknown. Run `openclaw triage` on the Gateway host and inspect the result before retrying.",
    triage: {
      hostHint:
        "If Ask OpenClaw is unavailable, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair. Diagnose the cause before retrying.",
    },
    report: {},
    failureReasons: {
      dirty: "Commit or stash changes, then retry.",
      noUpstream: "Set an upstream branch, then retry.",
      notGitInstall:
        "Not a git checkout. Run `openclaw update` from the CLI for a global reinstall.",
      notOpenclawRoot:
        "Run the update from an OpenClaw checkout or use the CLI global reinstall path.",
      depsInstallFailed: "Dependency install failed. Fix the install error and retry.",
      buildFailed: "Build failed. Fix the build error and retry.",
      buildDirty:
        "The selected revision's build changed checkout files. Retry with a revision that includes its generated artifacts.",
      uiBuildFailed: "The control UI rebuild failed. Fix the UI build error and retry.",
      globalInstallFailed:
        "The global package install did not verify on disk. Retry or reinstall from the CLI.",
      restartDisabled:
        "The update was not applied because gateway restarts are disabled. Enable restarts in config, then retry.",
      restartUnavailable:
        "This global install cannot be safely replaced while restarts are disabled and no supervisor is present.",
      externalSupervisorUpdateRequired:
        "This Gateway is managed by an external supervisor. Use your server or deployment's update workflow to update OpenClaw and restart the Gateway. The Control UI and `openclaw update` cannot update this installation. No package changes or Gateway restart were attempted.",
      restartUnhealthy:
        "The replacement process never became healthy. The previous process stayed up so you can recover.",
      restartRevisionMismatch:
        "The restarted Gateway is running a different revision. Check the service install root and retry.",
      restartRevisionUnavailable:
        "The restarted Gateway could not report its revision. Check the service install root and logs before retrying.",
      alreadyCurrent: "This checkout is already at its tracked upstream revision.",
      managedServiceHandoffAlreadyRunning:
        "Another managed update is already running. Wait for it to complete, then refresh update status.",
      managedServiceHandoffUnavailable:
        "Stop the foreground Gateway, run `openclaw update`, then launch it again. For automatic updates, install a managed Gateway service.",
      doctorFailed: "Doctor repair failed. Run `openclaw doctor --non-interactive` and retry.",
      managedServiceHandoffFailed:
        "The update helper stopped before finishing. Run `openclaw update` in the terminal to see why.",
      managedServiceHandoffSpawnFailed:
        "The Gateway could not start the update helper. Run `openclaw update` in the terminal instead.",
      managedServiceHandoffParentTimeout:
        "The Gateway stayed up too long for the update helper. Start the update again, or run `openclaw update`.",
      default: "See the gateway logs for the exact failure and retry once the cause is fixed.",
    },
    failedAtStep: "The update failed at {step}: {cause}.",
    succeededVersion: "Gateway updated to v{version}.",
    succeededCommit: "Gateway updated · now on {sha}.",
    succeeded: "Gateway updated and restarted.",
  },
  devices: {
    readOnly: {},
    // The trigger and load failure stay eager; dialog copy follows its lazy renderer.
    pairing: {
      button: "Pair device",
      adminRequired: "Administrator access is required to create setup codes.",
      title: "Pair a device",
      loadFailed: "Could not load the pairing dialog. Check your connection and try again.",
    },
    binding: {},
    inventory: {},
    // Lazy: en-devices.ts registers this subtree when the Devices page renders chips.
    capabilities: {},
    execApprovals: {},
  },
  worktrees: {
    adminRequired: "Browsing only. Worktree changes require operator.admin access.",
    newWorktree: "New worktree",
    ownerManual: "Manual",
    ownerSession: "Session",
    ownerWorkboard: "Workboard",
    title: "Managed Worktrees",
    subtitle: "Isolated repository checkouts owned by OpenClaw.",
    cleanNow: "Clean up now",
    name: "Name",
    namePlaceholder: "auto",
    baseBranch: "Base branch",
    baseBranchPlaceholder: "Remote default",
    repo: "Repository",
    empty: "No managed worktrees.",
    restorable: "Restorable",
    restore: "Restore",
    confirmDelete: "Snapshot and delete {name}?",
    confirmForceDelete: "Snapshot failed: {error}\n\nDelete without a snapshot?",
  },
  agentChip: agentEn.agentChip,
  agentScope: agentEn.agentScope,
  newSession: {
    title: "New session",
    hint: "Pick where this session works, then say what to do.",
    placementReloadBlocked: "Recovery needs a reload. Unsaved starts will be lost.",
    discardUnsavedAndReload: "Discard unsaved starts and reload",
  },
  dashboardsPage: {
    emptyTitle: "No dashboards yet",
    emptyDescription: "Dashboards created in your tasks will appear here.",
    loadError: "Could not load dashboards: {error}",
    openFocusMode: "Open dashboard in focus mode",
    live: "Live",
    byAuthor: "By {author}",
    updated: "Updated {time}",
    updatedUnknown: "Update time unknown",
    totalLabel: "dashboards",
    searchLabel: "Search dashboards",
    searchPlaceholder: "Search dashboards…",
    authorFilter: "Author",
    allAuthors: "All authors",
    sortLabel: "Sort",
    sortUpdated: "Recently updated",
    sortTitle: "Title A–Z",
    resultCount: "Dashboards: {count}",
    noResultsTitle: "No matching dashboards",
    noResultsDescription: "Try another search or author.",
  },
  dashboardDocument: {
    close: "Close dashboard",
    missingSession: "No dashboard session was specified.",
    notFound: "This session could not be found.",
    unavailable: "Session dashboards are unavailable for this connection.",
    loadFailed:
      "Could not load this dashboard: {error}. Check the Gateway connection and try again.",
  },
  sessionsView: {
    advanced: "Advanced",
    copyDetails: "Copy details",
    copyLink: "Copy link",
    archiveSessionOnly: "Session only",
    communication: {
      title: "Cross-session messaging",
      send: "Send messages",
      receive: "Receive messages",
      always: "Always",
      ask: "Ask",
      never: "Never",
      default: "default",
      sendDescription:
        "Allow this session to initiate messages to other sessions. Ask requires human approval; Never blocks them.",
      receiveDescription:
        "Allow other sessions to initiate messages to this session. Ask requires human approval; Never blocks them.",
      resetDescription: "Use the configured defaults for sending and receiving messages.",
    },
    subagentPrefix: "Subagent:",
    automationPrefix: "Automation:",
    actionRequiresConnection: "Connect to the Gateway to change sessions.",
    actionUnavailable: "This Gateway does not support this session action.",
    actionRequiresScope: "This action requires {scope} access.",
    actionRequiresOwnership: "Only the session creator or an admin can make this change.",
    deletePreservedReasons: {
      "owner-mismatch": "owned elsewhere",
      busy: "live run or cleanup active",
      "foreign-lock": "foreign Git lock",
      "snapshot-failed": "OpenClaw could not create a safety snapshot",
      "cleanup-failed": "cleanup failed",
    },
    draftCleanupFailed: "Session deleted; browser draft remains. Clear site data.",
    title: "Sessions",
    subtitle: "Browse sessions and manage per-session overrides.",
    store: "Store: {path}",
    active: "Updated within",
    limit: "Limit",
    createdBy: "Created by {name}",
    ownedBy: "Owned by {name}",
    archivedBy: "Archived by {name}",
    archiveReason: "Archive reason",
    archiveReasonManual: "Archived manually",
    archiveReasonAgeRetention: "Automatically archived by age retention",
    archiveReasonActiveSessionCap:
      "Automatically archived because the active-session limit was reached",
    archiveReasonStaleDashboard: "Automatically archived after dashboard inactivity",
    archiveReasonRestartRecovery: "Archived when a replacement session was recovered",
    viewingNow: "viewing now",
    owners: "Owners",
    allOwners: "All owners",
    involvingMe: "Involving me",
    hideFromInvolvingMe: "Hide from Involving me",
    showInInvolvingMe: "Show in Involving me",
    specificOwner: "Specific owner",
    specificOwnerAvailable: "Specific owner: {count} available",
    specificOwnerSelected: "Specific owner: {name}",
    ownerYou: "{name} (You)",
    withParticipant: "with {name}",
    withMoreParticipants: "+{count} more",
    assignToMe: "Me",
    assignTo: "Assign to…",
    filterControls: "Session filters",
    filters: "Filters",
    sourceFilters: "Session source filters",
    global: "Global",
    unknown: "Unknown",
    sessionState: "Session state",
    all: "All",
    snooze: "Snooze",
    wakeSession: "Wake session",
    snoozeChooseTime: "Choose another time",
    snoozeCustom: "Custom",
    snoozeDateTime: "Date and time",
    snoozeLocalTime: "Your local time ({zone})",
    snoozeFutureRequired: "Choose a date and time in the future.",
    snoozeHour: "In 1 hour",
    snoozeThreeHours: "In 3 hours",
    snoozeEvening: "This evening",
    snoozeTomorrow: "Tomorrow",
    snoozeNextWeek: "Next week",
    snoozed: "Snoozed",
    snoozeWakes: "Wakes {time}",
    snoozeTomorrowTime: "tomorrow {time}",
    sessionArchived: "Session archived",
    archiving: "Archiving…",
    deleteAllArchived: "Delete all archived…",
    deleteAllArchivedConfirm:
      "Delete {count} archived sessions and their transcripts? Any attached workers will be stopped safely first.",
    activeTooltip: "Loads sessions updated in the last {count} minutes.",
    limitTooltip: "Max sessions to load.",
    globalTooltip: "Include global sessions.",
    unknownTooltip: "Include unknown sessions.",
    archivedOnlyTooltip: "Show only archived sessions.",
    minutesPlaceholder: "min",
    searchPlaceholder: "Filter by key, agent, label, kind…",
    clearSearch: "Clear search",
    user: "User",
    assistant: "Assistant",
    selected: "{count} selected",
    deleteSelected: "Delete",
    selectAllOnPage: "Select all on page",
    selectSession: "Select session",
    optionalPlaceholder: "(optional)",
    key: "Key",
    label: "Label",
    kind: "Kind",
    updated: "Updated",
    tokens: "Tokens",
    goal: "Goal",
    goalNote: "Goal note",
    thinking: "Thinking",
    fast: "Fast",
    verbose: "Verbose",
    reasoning: "Reasoning",
    actions: "Actions",
    addToWorkboard: "Add to Workboard",
    openWorkboardCard: "Open Workboard card",
    opensAsDashboard: "Opens as dashboard",
    approvalNeeded: "Approval needed",
    messageNeedsAttention: "{count} message needs attention",
    messagesNeedAttention: "{count} messages need attention",
    unsentDraft: "Unsent draft",
    noSessions: "No sessions found.",
    noActiveSessions: "No active sessions.",
    noArchivedSessions: "No archived sessions.",
    noSessionsMatchFilters: "No sessions match your filters.",
    pagination: "{start}-{end} of {total} rows",
    pageSize: "Rows per page",
    rowsPerPage: "{count} per page",
    showAll: "Show all",
    inherit: "inherit",
    offExplicit: "off (explicit)",
    auto: "auto",
    on: "on",
    off: "off",
    full: "full",
    stream: "stream",
    contextUsage: "{percent}% of context used ({used} / {context} tokens)",
    promptBudgetUsage: "{percent}% of last-run prompt budget used ({used} / {context} tokens)",
    promptBudgetUsageApprox:
      "Approximately {percent}% of last-run prompt budget used ({used} / {context} tokens)",
    contextUsageApprox: "~{percent}% of context used ({used} / {context} tokens, approximate)",
    showSessionDetails: "Show session details for {count}",
    hideSessionDetails: "Hide session details for {count}",
    sessionDetails: "Session details",
    overrides: "Overrides",
    status: "Status",
    statusLive: "Live",
    statusIdle: "Idle",
    statusUnknown: "Unknown",
    statusQueued: "Queued",
    statusRunning: "Running",
    statusDone: "Done",
    statusFailed: "Failed",
    statusInterrupted: "Interrupted",
    statusKilled: "Killed",
    statusTimeout: "Timed out",
    waitingForAnswer: "Waiting for your answer",
    waitingForApproval: "Waiting for approval",
    questionsNeedAnswer: "{count} questions need your answer",
    approvalsNeedApproval: "{count} requests need approval",
    attentionMore: "+{count} more",
    runFailedReason: "Run failed: {reason}",
    childRunFailedReason: "Child session {label} failed: {reason}",
    runErrorTimedOut: "Timed out",
    runErrorUnknown: "Unknown error",
    attentionRequired: "Session needs attention",
    openSession: "Open session",
    openFailed: "Could not open this session. Try again.",
    model: "Model",
    provider: "Provider",
    runtime: "Runtime",
    runDuration: "Run duration",
    lastActive: "Last active {time}",
    surface: "Surface",
    subject: "Subject",
    room: "Room",
    space: "Space",
    sessionId: "Session ID",
    activeRun: "Active run",
    subagentsWorking: "Subagents working",
    showChildSessions: "Show {count} child sessions for {session}",
    hideChildSessions: "Hide {count} child sessions for {session}",
    childSessions: "Child sessions",
    showMoreChildren: "Show {count} more",
    archived: "Archived",
    pinned: "Pinned",
    unread: "Unread",
    automationAttached: "Automation attached",
    incognito: "Incognito session",
    incognitoLabel: "Incognito",
    incognitoDescription:
      "Incognito session. History expires 24 hours after creation or when the Gateway restarts, whichever comes first.",
    incognitoLimits:
      "Your model provider still processes messages. Tools, plugins, and uploads may save data.",
    cloudWorkerPlacement: "Placement: {state}",
    placementWorkspaceConflict: "{placement} · 1 workspace conflict",
    placementWorkspaceConflicts: "{placement} · {count} workspace conflicts",
    cloudWorkerDiskWarning: "Cloud session disk space is low",
    cloudWorkerDiskCritical: "Cloud session disk space is critically low",
    cloudWorkerDescendantConflict: "Cloud worker child: 1 workspace conflict",
    cloudWorkerDescendantConflicts: "Cloud worker children: {count} workspace conflicts",
    renameSession: "Rename session",
    renameSessionMenu: "Rename…",
    setIconMenu: "Set icon",
    setColorMenu: "Color",
    setIconColorMenu: "Icon & color",
    sessionColor: "Session color: {color}",
    colors: {
      red: "Red",
      blue: "Blue",
      green: "Green",
      yellow: "Yellow",
      purple: "Purple",
      orange: "Orange",
      pink: "Pink",
      cyan: "Cyan",
    },
    iconEmojiSection: "Emoji",
    iconGlyphSection: "Icons",
    noIcon: "No icon",
    noColor: "No color",
    customEmojiCell: "Custom emoji…",
    customEmojiTitle: "Custom emoji",
    customEmojiSet: "Set",
    customEmojiHint: "Any emoji works. Press {shortcut} for the system emoji picker.",
    customEmojiHintNoShortcut: "Any emoji works.",
    customIconCell: "Custom icon…",
    customIconTitle: "Custom icon",
    customIconHint: "Paste an emoji or SVG. Press {shortcut} for the system emoji picker.",
    customIconHintNoShortcut: "Paste an emoji or SVG.",
    removeIcon: "Remove icon",
    pinSession: "Pin session",
    pinRootSessionsOnly: "Only root sessions can be pinned; pin the parent session instead.",
    unpinSession: "Unpin session",
    markUnread: "Mark as unread",
    markRead: "Mark as read",
    markUnreadCount: "Mark {count} as unread",
    markReadCount: "Mark {count} as read",
    forkSession: "Fork conversation",
    forkFromLastCompleted: "Fork from last completed message",
    forkedSession: "Forked session",
    copySessionId: "Session ID",
    copySessionLink: "Session link",
    copySessionPreviewLink: "Preview link",
    copyMarkdown: "Conversation as Markdown",
    openNewTab: "New tab",
    openNewWindow: "New window",
    splitBelow: "Split below",
    workspaceEditors: "Workspace · editor",
    resetAppearance: "Reset to default",
    copyTranscriptChanged: "Conversation changed while copying. Try again.",
    openWindowBlocked: "Allow pop-ups for this site, then try again.",
    splitUnavailable: "Open a conversation on a wide screen to use split view.",
    openPullRequest: "Open PR",
    openInEditorMenu: "Open in",
    archiveSession: "Archive session",
    archiveSessionCount: "Archive {count}",
    restoreSession: "Restore session",
    restoreSessionCount: "Restore {count}",
    moveSession: "Move session…",
    moveSessionTitle: "Move session",
    moveSessionDescription: 'Choose where "{session}" should continue.',
    moveSessionNoReplayWarning:
      "OpenClaw safely reconciles the current workspace before moving. Active work is never replayed.",
    moveSessionActiveRunWarning:
      "The active turn will be interrupted. Partial output is not replayed; send the next turn again after the move.",
    moveSessionAction: "Move session",
    moveSessionCatalogFailed: "Could not load available destinations.",
    moveSessionFailed: "Move failed",
    moveSessionGatewayTarget: "Gateway",
    movingSession: "Moving to {target}…",
    movingSessionGeneric: "Moving session…",
    stopCloudWorker: "Stop cloud worker…",
    initialTurnPausedByWorkerStop:
      "Worker stop requested. Review the initial message before retrying.",
    deleteSessionMenu: "Delete…",
    deleteSessionCount: "Delete {count}…",
    deleteSessionStale: 'Gateway connection replaced before "{session}" was deleted. Try again.',
    deleteSessionsStale:
      "Gateway connection replaced before {count} sessions were deleted. Try again.",
    groupBy: "Group by",
    groupByNone: "None",
    groupByCategory: "Custom groups",
    groupByPerson: "Person",
    showSessionPreview: "Show message preview",
    hideEmptyGroups: "Hide empty groups",
    hideEmptyGroupsSelected: "Hide empty groups: {mode}",
    emptyGroupsWhenFiltering: "When filtering",
    emptyGroupsAlways: "Always",
    emptyGroupsNever: "Never",
    showCronSessions: "Show automation sessions",
    showSystemSessions: "Show system sessions",
    groupByChannel: "Channel",
    groupByKind: "Kind",
    groupByAgent: "Agent",
    groupByDate: "Date",
    group: "Group",
    ungrouped: "Ungrouped",
    newGroup: "New group",
    newGroupPrompt: "New group name",
    newGroupTitle: "New group",
    newGroupCreate: "Create group",
    newGroupFailed: "Could not create the group.",
    newGroupStale: "Gateway connection replaced before the group was saved. Try again.",
    moveToGroup: "Move session to a group",
    moveToGroupMenu: "Move to group",
    moveToGroupMenuCount: "Move {count} to group",
    removeFromGroup: "Remove from group",
    moveBackToGroups: "Move back to Groups",
    groupMenu: "Group options for {group}",
    newSessionInGroup: "New session in {group}",
    groupDefaultsMenu: "New session defaults",
    groupDefaultsFailed: "Could not save the group defaults.",
    groupDefaultsStale: "Gateway connection replaced before the defaults were saved. Try again.",
    renameGroupMenu: "Rename group",
    renameGroupTitle: 'Rename group "{group}"',
    groupNameLabel: "Group name",
    deleteGroupMenu: "Delete group",
    deleteGroupTitle: 'Delete group "{group}"',
    deleteGroupConfirm: "The group is removed. Its sessions move back to the session list.",
    deleteGroupStale: 'Gateway connection replaced before "{group}" was deleted. Try again.',
    dragSessionHint: "Drag session to move between groups",
    dateToday: "Today",
    dateYesterday: "Yesterday",
    dateThisWeek: "This week",
    dateOlder: "Older",
    dateNoActivity: "No activity",
    groupRowCount: "{count} sessions",
    groupRowCountOne: "{count} session",
  },
  agents: {
    noAgents: "No agents",
    createdBy: "Created by {id}",
    defaults: {
      title: "Agent defaults",
      description: "Defaults every agent inherits unless overridden.",
    },
    copyId: "Copy ID",
    default: "Default",
    setDefault: "Set Default",
    ...agentEn.pinning,
    selectTitle: "Select an agent",
    selectSubtitle: "Pick an agent to inspect its workspace and tools.",
    tabs: {
      overview: "Overview",
      files: "Files",
      tools: "Tools",
      skills: "Skills",
      channels: "Channels",
      cronJobs: "Automations",
      memory: "Memory",
    },
    context: {
      title: "Agent Context",
      workspace: "Workspace",
      openFilesTab: "Open Files tab",
      primaryModel: "Primary Model",
      runtime: "Runtime",
      thinkingDefault: "Thinking Default",
      identityName: "Identity Name",
      identityAvatar: "Identity Avatar",
      skillsFilter: "Skills Filter",
      default: "Default",
      configurationSubtitle: "Workspace, identity, and model configuration.",
      schedulingSubtitle: "Workspace and scheduling targets.",
    },
    identity: agentEn.identity,
    overview: {
      title: "Overview",
      subtitle: "Workspace paths and identity metadata.",
      selectedSkills: "{count} selected",
      allSkills: "all skills",
      unsavedConfig: "You have unsaved config changes.",
      modelSelection: "Model Selection",
      primaryModel: "Primary model",
      primaryModelDefault: "Primary model (default)",
      notSet: "Not set",
      inheritDefault: "Inherit default",
      inheritDefaultModel: "Inherit default ({model})",
      fallbacks: "Fallbacks",
      addFallback: "Add fallback…",
    },
    toolCatalog: {
      groups: {
        files: "Files",
        runtime: "Runtime",
        web: "Web",
        memory: "Memory",
        sessions: "Sessions",
        ui: "UI",
        messaging: "Messaging",
        automation: "Automation",
        nodes: "Nodes",
        agents: "Agents",
        media: "Media",
      },
      descriptions: {
        read: "Read file contents",
        write: "Create or overwrite files",
        edit: "Make precise edits",
        applyPatch: "Patch files (OpenAI)",
        exec: "Run shell commands",
        process: "Manage background processes",
        webSearch: "Search the web",
        webFetch: "Fetch web content",
        memorySearch: "Semantic search",
        memoryGet: "Read memory files",
        sessionsList: "List sessions",
        sessionsHistory: "Session history",
        sessionsSend: "Send to session",
        sessionsSpawn: "Spawn sub-agent",
        sessionStatus: "Session status",
        browser: "Control web browser",
        canvas: "Control canvases",
        message: "Send messages",
        cron: "Schedule tasks",
        gateway: "Gateway control",
        nodes: "Nodes + devices",
        agentsList: "List agents",
        image: "Image understanding",
      },
      profiles: {
        minimal: "Minimal",
        coding: "Coding",
        messaging: "Messaging",
        full: "Full",
      },
    },
    skillsPanel: {
      title: "Skills",
      subtitle: "Per-agent skill allowlist and workspace skills.",
      loadConfig: "Load the gateway config to set per-agent skills.",
      customAllowlist: "This agent uses a custom skill allowlist.",
      inheritedAllowlist: "This agent inherits the default skill allowlist.",
      allEnabled: "All skills are enabled. Disabling any skill will create a per-agent allowlist.",
      loadAgent: "Load skills for this agent to view workspace-specific entries.",
      filter: "Filter",
      searchPlaceholder: "Search skills",
      shown: "{count} shown",
      empty: "No skills found.",
      missing: "Missing: {items}",
      reason: "Reason: {items}",
      learnedAlwaysOn: "Learned skill — archive in Workshop to hide",
      updateError: "Could not update the agent skill allowlist.",
    },
    channels: {
      title: "Channels",
      subtitle: "Gateway-wide channel status snapshot.",
      lastRefresh: "Last refresh: {time}",
      loadHint: "Load channels to see live status.",
      empty: "No channels found.",
      connectedCount: "{connected}/{total} connected",
      noAccounts: "no accounts",
      configuredCount: "{count} configured",
      notConfigured: "not configured",
      enabledCount: "{count} enabled",
      setupGuide: "Setup guide",
    },
    cronPanel: {
      schedulerTitle: "Scheduler",
      schedulerSubtitle: "Gateway automations status.",
      jobs: "Jobs",
      nextWake: "Next wake",
      agentJobsTitle: "Agent Automations",
      agentJobsSubtitle: "Scheduled jobs targeting this agent.",
      noJobs: "No jobs assigned.",
      runNow: "Run Now",
      edit: "Edit",
      editJob: "Edit {name}",
    },
    files: {
      emptyDraft: "Empty draft",
      minRead: "{count} min read",
      markdownPreview: "Markdown Preview",
      extensionPreview: "{ext} Preview",
      preview: "Preview",
      expandPreview: "Expand preview",
      collapsePreview: "Collapse preview",
      editFile: "Edit file",
      closePreview: "Close preview",
      coreFilesTitle: "Core Files",
      coreFilesSubtitle: "Bootstrap persona, identity, and tool guidance.",
      workspace: "Workspace",
      loadHint: "Load the agent workspace files to edit core instructions.",
      empty: "No files found.",
      missing: "missing",
      selectFile: "Select a file to edit.",
      willCreateOnSave: "Will Create on Save",
      liveDraftPreview: "Live Draft Preview",
      savedPreview: "Saved Preview",
      updated: "Updated {time}",
      notCreatedYet: "Not Created Yet",
      updatedUnknown: "Updated Unknown",
      missingHint: "This file is missing. Saving will create it in the agent workspace.",
      addFile: "Add file…",
      createHint: "This file does not exist yet. Saving will create it in the agent workspace.",
      content: "Content",
      words: "{count} words",
      lines: "lines",
      overwrite: "Overwrite",
      conflictHint:
        "This file changed in the agent workspace after this draft was started. Reload to take the workspace version, or Overwrite to replace it with this draft.",
    },
  },
  debug: {
    lanes: {
      lane: "Lane",
      active: "Active",
      queued: "Queued",
      blocked: "Blocked",
    },
    overlay: {
      title: "System busyness",
      eyebrow: "Live diagnostics",
      move: "Move system busyness with arrow keys, or drag",
      minimize: "Minimize system busyness",
      expand: "Expand system busyness",
      lanes: "Lanes",
      status: "Event loop / status",
      activeRuns: "Active runs",
      events: "Events",
      cpu: "CPU",
      memory: "Memory",
      delayP99: "Delay p99",
    },
  },
  configForm: {
    redactedPlaceholder: "[redacted - click reveal to view]",
    sectionHelp: "Help for {section}",
    advancedDivider: "Advanced",
    advancedSettings: "Advanced settings",
    hideValue: "Hide value",
    revealValue: "Reveal value",
    disableStreamToReveal: "Disable stream mode to reveal value",
    storedSecretNotRevealable: "This editor cannot reveal the stored value.",
    unsupportedType: "Unsupported type: {type}. Use Raw mode.",
    structuredSecretRaw: "Structured value (SecretRef) - use Raw mode to edit",
    structuredSecretFile: "Structured value (SecretRef) - edit the config file directly",
    defaultValue: "Default: {value}",
    select: "Select...",
    enumOn: "On",
    enumOff: "Off",
    enumAuto: "Auto",
    nullValue: "null",
    jsonValue: "JSON value",
    invalidJson: "Enter valid JSON before leaving this field.",
    invalidString: "Enter a value that matches this setting's constraints.",
    invalidNumber: "Enter a value within the allowed range and step.",
    draftRejected: "This setting could not be saved. Your draft is still here.",
    unsupportedArray: "Unsupported array schema. Use Raw mode.",
    itemCountOne: "{count} item",
    itemCount: "{count} items",
    add: "Add",
    noItems: 'No items yet. Click "Add" to create one.',
    removeItem: "Remove item",
    customEntries: "Custom entries",
    addEntry: "Add Entry",
    key: "Key",
    removeEntry: "Remove entry",
    renameRedactedBlocked:
      "This entry holds a stored secret. Add the new key with its value, then remove this one.",
    schemaUnavailable: "Schema unavailable.",
    unsupportedSchema: "Unsupported schema. Use Raw.",
    unsupportedNode: "Unsupported schema node. Use Raw mode.",
    noSettingsMatch: 'No settings match "{query}"',
    noSettingsInSection: "No settings in this section",
    sections: {},
  },
  // Historical namespace from the pre-restructure "quick settings" page; its
  // keys now serve Models, Privacy & Security, Appearance, and Profile.
  // Renaming would force retranslation of every key, so the name stays.
  quickSettings: {
    language: "Language",
    model: {
      title: "Model & Thinking",
      model: "Model",
      thinking: "Thinking",
      fastMode: "Fast Mode",
      default: "Default",
      modelPolicy: "Model policy",
      thinkingLevels: {
        off: "Off",
        low: "Low",
        medium: "Medium",
        high: "High",
      },
      fastModes: {
        auto: "Auto",
        fast: "Fast",
        standard: "Standard",
        on: "On",
        off: "Off",
      },
    },
    channels: {
      title: "Channels",
      connect: "Connect →",
    },
    appearance: {
      lobsterVisits: "Lobster visits",
      lobsterVisitsOn: "Drops by occasionally",
      lobsterVisitsThemeHidden:
        "The lobster stays home while the {theme} theme is active. Other visitors still drop by.",
      lobsterVisitsOff: "Never visits",
      lobsterSounds: "Lobster sounds",
      lobsterSoundsOn: "Tiny blubs when touched",
      lobsterSoundsOff: "Silent",
      lobsterdex: "Lobsterdex",
      lobsterdexSeen: "{seen}/{total} visited",
      lobsterdexFirstVisited: "{name} · first visited {date}",
      lobsterdexCardFirstVisited: "First visited {date}",
      lobsterdexCardShinySeen: "✦ Shiny spotted {date}",
      lobsterdexCardCopyLink: "Copy link",
      lobsterdexOpen: "Open Lobsterdex",
      lobsterdexThemeHidden:
        "This theme hides Lobsterdex. Your collection and preferences are preserved.",
    },
    security: {
      intro: "Review gateway access, tool policy, device authentication, and approvals.",
      title: "Security",
      gatewayAuth: "Gateway auth",
      execPolicy: "Exec policy",
      browserEnabled: "Browser enabled",
      toolProfile: "Available tools",
      toolProfileDefault:
        "Using core and default plugin tools. Choose Full to include available optional plugin tools.",
    },
    system: {
      gatewayHost: "Gateway Host",
      up: "Up {duration}",
      cpu: "CPU",
      memory: "Memory",
      disk: "Disk",
      usage: "{label} usage",
      loadAverage: "Load average: {values}",
      core: "{count} core",
      cores: "{count} cores",
      load: "load",
      used: "used",
      freeOf: "{free} free of {total}",
      runtime: "Node {version} · PID {pid}",
    },
    personal: {
      browserOnly: "Stored in this browser only.",
    },
  },
  configPage: {},
  configView: {
    adminRequired: "Configuration changes require operator.admin access.",
    channelSettings: "Channel settings",
    categories: {},
    sections: {},
    themes: {},
    textSizes: {},
    profileSyncedHint: "Saved to your profile — follows you on every device.",
    syncedHint: "Synced across your devices through the gateway.",
    syncPendingHint: "Waiting to sync through the gateway.",
    notifications: {},
    appearance: {},
    chatPrefs: {},
    sidebarPrefs: {},
    sessionObserver: {},
    connection: {},
    root: "<root>",
    itemCount: "[{count} item]",
    itemCountPlural: "[{count} items]",
    form: "Form",
    raw: "Raw",
    formUnsafeTitle: "Form view can't safely edit some fields",
    rawTitle: "Edit raw JSON/JSON5 config",
    rawUnavailableTitle: "Raw mode unavailable for this snapshot",
    open: "Open",
    fileOpenedOnGateway: "Configuration file opened on Gateway host.",
    applying: "Applying…",
    autoSaveSaving: "Saving…",
    autoSaveSaved: "Saved",
    autoSavePaused: "Autosave paused after reconnect",
    saveNow: "Save",
    autoSaveFailed: "Save failed",
    autoSaveRejected: "Settings not applied",
    autoSaveRejectedHint: "Current settings are unchanged.",
    rejectionDetails: "Show reason",
    recoveryNotRestored:
      "Settings could not be restored. Your draft is kept. Check {path} before saving again.",
    recoveryUnknown:
      "Settings restoration could not be confirmed. Your draft is kept. Check {path} before saving again.",
    recoveryBackup: "Inspect the recovery backup at {path}.",
    recoveryReload: "Discard draft and reload",
    discardUnconfirmed:
      "The last save could not be confirmed. Your draft is kept. Reload configuration before discarding this setting.",
    writeGatewayChanged:
      "This unsettled change belongs to a different Gateway. Reconnect to that Gateway to retry, or discard the draft.",
    writeUnconfirmed:
      "The last configuration change could not be confirmed. Your draft is kept. Retry before changing other settings.",
    autoSaveConflict: "Settings changed elsewhere",
    retry: "Retry",
    applyChanges: "Apply changes",
    rawDiscard: "Discard",
    reloadBlocked: "Finish saving or discard pending config changes before reloading.",
    rawDraftBlocksApply: "Save or discard edits in the Raw editor before applying changes.",
    rawDraftPendingFormTitle:
      "Unsaved raw config edits — save or discard them before switching to Form.",
    rawDraftBlocksFormEdit:
      "Unsaved raw config edits could not be parsed; resolve them in the Raw editor before changing settings.",
    rawDraftUnverified:
      "Cannot verify the raw draft against the saved configuration; reload and retry.",
    invalidConfig: "Your configuration is invalid. Some settings may not work as expected.",
    dismissWarning: "Don't remind again",
    viewPendingChangesRaw: "View pending changes",
    rawDiffUnavailable: "Changes detected (JSON diff not available)",
    hideEnvValues: "Hide env values",
    revealEnvValues: "Reveal env values",
    peek: "Peek",
    loadingSchema: "Loading schema…",
    formUnsafeCount: "1 setting in this config can only be edited as text: {paths}",
    formUnsafeCountPlural: "{count} settings in this config can only be edited as text: {paths}",
    formUnsafeMore: "+{count} more",
    openRawEditor: "Open Raw editor",
    rawConfig: "Raw config (JSON/JSON5)",
    secretCount: "{count} secret",
    secretCountPlural: "{count} secrets",
    redacted: "redacted",
    visible: "visible",
    revealSensitive: "Reveal sensitive values",
    hideSensitive: "Hide sensitive values",
    toggleRawRedaction: "Toggle raw config redaction",
    sensitiveHidden:
      "{count} sensitive value hidden. Use the reveal button above to edit the raw config.",
    sensitiveHiddenPlural:
      "{count} sensitive values hidden. Use the reveal button above to edit the raw config.",
  },
  execApproval: {
    scope: {
      standingGrant:
        'Always allow runs this exact command for "{automation}" without asking, until revoked (revocable)',
      standingGrantDays:
        'Always allow runs this exact command for "{automation}" without asking, for {count} days (revocable)',
      messageSend: "Sends to {count} recipient(s) via {target}",
      payment: "Pays {amount} {currency} to {target}",
      externalPost: "Posts externally to {target}",
    },
    expiresIn: "expires in {time}",
    expired: "expired",
    execApprovalNeeded: "Exec approval needed",
    pluginApprovalNeeded: "Plugin approval needed",
    requestedBySession: "Approval requested by session {session}",
    pending: "{count} pending",
    otherPending: "Other pending requests",
    reviewRequest: "Review approval from {agent}: {command}",
    decisionRequest: "{decision}: {command}",
    agentPendingOne: "{count} pending approval",
    agentPending: "{count} pending approvals",
    allowOnce: "Allow once",
    alwaysAllow: "Always allow",
    alwaysAllowHere: "Always allow here",
    allowAlwaysUnavailable: "Allow Always is unavailable for this command.",
    reviewOnly: "Review only. Sign in with approval access to record a decision.",
    deny: "Deny",
    details: "Details",
    labels: {
      host: "Host",
      session: "Session",
      cwd: "CWD",
      resolved: "Resolved",
      security: "Security",
      ask: "Ask",
    },
  },
  approvalPage: {
    brandName: "OpenClaw",
    eyebrow: "Operator approval",
    loadingTitle: "Loading approval",
    loadingDescription: "Checking the current approval state with the Gateway.",
    unavailableTitle: "Approval unavailable",
    unavailableDescription:
      "This approval could not be found or this device is not authorized to review it.",
    connectionErrorTitle: "Connection interrupted",
    connectionErrorDescription:
      "OpenClaw cannot confirm or record a decision while disconnected. Reconnect to check the current status.",
    retry: "Retry",
    execTitle: "Command approval",
    pending: "Waiting for your decision",
    pendingDescription: "Review the request carefully. The first answer from any surface wins.",
    approvedHere: "Approved here",
    deniedHere: "Denied here",
    resolvedElsewhere: "Resolved elsewhere",
    resolvedElsewhereDescription:
      "Another surface or an earlier attempt recorded the decision first.",
    approved: "Approved",
    denied: "Denied",
    expired: "Expired",
    cancelled: "Cancelled",
    allowedOnceDescription: "The operation was approved for this request only.",
    allowedAlwaysDescription: "The operation was approved with the always-allow decision.",
    deniedDescription: "The operation was denied and will not continue.",
    expiredDescription: "No decision arrived before the deadline, so the operation was denied.",
    cancelledDescription: "The requesting run ended before a decision could be used.",
    summaryLabel: "Summary",
    commandLabel: "Command",
    requestLabel: "Request details",
    nodeLabel: "Node",
    expiresLabel: "Expires",
    resolvedLabel: "Resolved",
    actionsLabel: "Approval decisions",
    resolvingDecision: "Recording {decision}…",
    safeToClose: "The decision is recorded. You can close this page.",
    openControlUi: "Open Control UI",
  },
  agentTools: {},
  skillGroups: {
    workspace: "Workspace Skills",
    learned: "Learned Skills",
    builtIn: "Built-in Skills",
    installed: "Installed Skills",
    extra: "Extra Skills",
    other: "Other Skills",
  },
  skillsPage: {},
  skillStatus: {
    bundled: "bundled",
    eligible: "eligible",
    blocked: "blocked",
    disabled: "disabled",
    blockedAllowlist: "blocked by allowlist",
    blockedAgentFilter: "blocked by agent filter",
  },
  nav: {
    gateway: {
      sectionLabel: "Gateway",
      menuLabel: "Gateway: {gateway}",
      primaryTag: "primary",
      setPrimary: "Set as primary…",
      openSettings: "Gateway settings…",
      connected: "Connected",
      unreachable: "Unreachable",
      unknown: "Unknown status",
    },
    owner: "Owner",
    back: "Back",
    forward: "Forward",
    settings: "Settings",
    askOpenClaw: "Ask OpenClaw",
    askBrand: "Ask {brand}",
    settingsGroupDevice: "This Mac",
    settingsGroupThisComputer: "This computer",
    settingsGroupThisIPhone: "This iPhone",
    settingsGroupThisIPad: "This iPad",
    settingsGroupThisDevice: "This device",
    settingsGroupConnections: "Connections",
    settingsGroupAgents: "Agents & Tools",
    settingsGroupSecurity: "Privacy & Security",
    settingsGroupSystem: "System",
    settingsSearchLabel: "Search settings",
    settingsSearchPlaceholder: "Search settings…",
    settingsSearchNoResults: "No matching settings.",
    settingsSearchClear: "Clear settings search",
    settingsLoadFailed: "Settings navigation could not load.",
    exitSettings: "Back to app",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    resize: "Resize sidebar",
    more: "More",
    home: "Home",
    pages: "Pages",
    pin: "Pin",
    unpin: "Unpin",
    customize: "Edit pinned items",
    customizeReset: "Reset pinned items",
    workboardGroup: "WorkBoard",
  },
  terminal: {
    title: "Terminal",
    copiedToClipboard: "Copied to clipboard",
    toggle: "Toggle terminal",
    open: "Open terminal",
    openWindow: "Open terminal in new window",
    hide: "Hide terminal",
    resize: "Resize terminal panel",
    newSession: "New terminal session",
    addFiles: "Add files to terminal",
    dropFiles: "Drop files to add them",
    uploadProgress: "Uploading {current} of {total}",
    uploadFailed: "Upload failed",
    retryUpload: "Retry",
    insertUploadedPaths: "Insert uploaded paths",
    uploadExpired: "Uploaded files may have expired. Cancel this batch and choose the files again.",
    closeSession: "Close terminal session",
    sessions: "Terminal sessions",
    refreshSessions: "Refresh",
    loadingSessions: "Loading sessions…",
    noSessions: "No live terminal sessions",
    currentSession: "current",
    sessionAttached: "attached",
    attachSession: "Attach session",
    attachFailed: "Could not attach terminal session",
    startCancelled: "Terminal start was cancelled. Try again.",
    connecting: "Connecting to session…",
    connectionTimedOut: "Session did not connect within 30 seconds.",
    refreshRequired: "Control UI updated. Reload this page to continue the terminal action.",
    tabLabel: "shell {n}",
    tabHint: "{agent} · {cwd}",
    agentOwnedBadge: "agent",
    exited: "exited",
    exitedCode: "exited ({code})",
    exitedSignal: "exited (signal {signal})",
    detached: "detached",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    dockMain: "Fill main content area",
    dockMode: "Terminal panel position",
    unavailable: "The terminal is not available on this gateway.",
    unusableSession:
      "The Gateway returned an unusable terminal session (missing {field}). The Gateway is likely older than this Control UI — update it, then retry.",
    uploadTooLarge: "File exceeds the 16 MiB terminal upload limit: {file}",
    uploadUnsafeCmdPath: "Cannot safely insert an uploaded path containing % or ! into cmd.exe",
    uploadUnsupportedShell: "Cannot safely insert an uploaded path into unsupported shell: {shell}",
    uploadInvalidNativePath: "Cannot safely insert the uploaded native file path",
  },
  browser: {
    nativeTab: "App tab",
    remoteTab: "Agent browser tab",
    stop: "Stop loading",
    profile: "Browser profile: {profile}",
    title: "Browser",
    open: "Open",
    openPanel: "Open browser panel",
    moreActions: "More actions",
    copyUrl: "Copy URL",
    openNewTab: "Open in new tab",
    openWithinOpenClaw: "Open in OpenClaw",
    toggle: "Toggle browser panel",
    close: "Close browser panel",
    resize: "Resize browser panel",
    newTab: "New tab",
    closeTab: "Close tab",
    untitledTab: "New tab",
    tweetPost: "Post on X",
    openPost: "Open post",
    back: "Back",
    forward: "Forward",
    reload: "Reload",
    openExternal: "Open in your browser",
    urlPlaceholder: "Enter a URL and press Enter",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    annotate: "Annotate page",
    inspect: "Inspect element",
    annotateSend: "Send to chat",
    annotateUndo: "Undo",
    annotateClear: "Clear",
    annotateDone: "Exit annotate mode",
    annotateHint: "Draw on the page, then send the markup to your chat.",
    inspectName: "Name",
    inspectRole: "Role",
    inspectFocusable: "Focusable",
    loading: "Loading page…",
    notRunning: "The gateway browser is not running.",
    start: "Start browser",
    errors: {},
    annotatePrompt: {},
  },
  desktop: {
    title: "Desktop",
    openWindow: "Open desktop in new window",
    unavailable: "Desktop viewing is unavailable for this connection.",
    toggle: "Toggle desktop panel",
    reconnect: "Reconnect",
    connecting: "Connecting to desktop…",
  },
  systems: {},
  routeTitles: {
    modelProviders: "Models",
    notifications: "Notifications",
    advanced: "Advanced",
  },
  tabs: {
    device: "This Mac",
    devicePermissions: "Permissions",
    agentsHome: "Agents",
    agents: "Agents",
    activity: "Activity",
    meetings: "Meetings",
    apps: "Apps",
    portals: "Portals",
    approvals: "Approvals",
    workboard: "Workboard",
    worktrees: "Worktrees",
    channels: "Channels",
    connection: "Gateway",
    sessions: "Sessions",
    systems: "Systems",
    usage: "Usage",
    cron: "Automations",
    skills: "Skills",
    plugins: "Plugins",
    skillWorkshop: "Skill workshop",
    devices: "Devices",
    cloudWorkers: "Cloud environments",
    chat: "Chat",
    dashboards: "Dashboards",
    custodian: "OpenClaw",
    profile: "Profile",
    communications: "Communications",
    appearance: "Appearance",
    lobsterdex: "Lobsterdex",
    automation: "Automation",
    mcp: "MCP",
    memory: "Memory",
    search: "Search",
    talk: "Talk",
    infrastructure: "Infrastructure",
    labs: "Labs",
    updates: "Updates",
    about: "About",
    aiAgents: "Agent Defaults",
    modelSetup: "Model Setup",
    memoryImport: "Import Memory",
    security: "Privacy & Security",
    secrets: "Secrets",
    debug: "Debug",
    logs: "Logs",
    plugin: "Plugin",
  },
  subtitles: {
    device: "App settings and capabilities on this Mac.",
    devicePermissions: "Manage macOS access, location, and computer presence.",
    agentsHome: "Who is on your team and what they are doing",
    agents: "Workspaces, tools, identities.",
    activity: "Recent sessions across people using this gateway.",
    meetings: "Meeting notes and transcripts across this gateway.",
    apps: "Companion apps for phone, watch, desktop, and browser.",
    portals: "Live previews from agent-run applications.",
    approvals: "Recent exec, plugin, and system-agent approvals.",
    workboard: "Agent work queue and session handoff.",
    worktrees: "Isolated agent task checkouts and recovery snapshots.",
    channels: "Channels and settings.",
    connection: "Gateway endpoint, credentials, and handshake status.",
    sessions: "Active sessions and defaults.",
    systems: "Machines and desktops.",
    usage: "API usage and costs.",
    cron: "Scheduled tasks and recurring agent runs.",
    skills: "Manage your agent skills",
    plugins: "Extend your Claw with tools",
    skillWorkshop: "Skills your agent learned, recent changes, and undo.",
    devices: "Paired devices, pairing approvals, and exec bindings.",
    cloudWorkers: "Repository snapshots and machines for cloud sessions.",
    chat: "Gateway chat for quick interventions.",
    dashboards: "Tasks with saved dashboards.",
    custodian: "System setup and care.",
    config: "Legacy settings route; opens Appearance.",
    profile: "Your display name, avatar, and identity on this gateway.",
    communications: "Messages, text-to-speech, and meeting capture settings.",
    appearance: "Theme and UI settings.",
    lobsterdex: "Every lobster palette that has visited this browser.",
    automation: "Commands, hooks, automations, and plugins.",
    mcp: "MCP servers, auth, tools, and diagnostics.",
    memory: "Memory engine, search, and dreaming.",
    search: "Choose how agents search the web and check provider health.",
    talk: "Realtime voice: provider, model, and speaker voice.",
    infrastructure: "Gateway, browser, node host, discovery, and ACP settings.",
    labs: "Experimental agent and tool capabilities.",
    updates: "Release channel, automatic updates, and current update status.",
    about: "Control UI and connected Gateway build identity.",
    aiAgents: "Global agent defaults: skills, tools, and session.",
    modelSetup: "Connect a verified AI model",
    modelProviders: "Default models, behavior, provider access, usage, and cost.",
    memoryImport: "Bring Codex and Claude Code memory into an agent workspace.",
    notifications: "Browser push notifications from your gateway.",
    security: "Gateway auth, exec policy, tool profile, and approvals.",
    advanced: "Every remaining config section, plus the raw file editor.",
    debug: "Snapshots, events, RPC.",
    logs: "Live gateway logs.",
    plugin: "Plugin-provided panel.",
  },
  meetingCapture: {
    title: "Meeting capture",
    description: "Choose which sources can save meeting notes on this Gateway.",
    sources: "Auto-start sources",
  },
  cloudWorkersPage: {},
  portalsPage: {},
  modelSetup: {
    discovery: {},
    missingAuth: "No provider credential is configured for this model. Set it up in Model Setup.",
    commandHint: "Try /models or /help.",
    heading: "Connect a verified AI model",
    intro:
      "OpenClaw discovers AI access on this Gateway. Choose a provider to begin; nothing is selected, tested, installed, or saved automatically.",
    required: {
      title: "No AI provider configured",
      body: "Connect a provider and choose a model to send messages.",
      action: "Connect an AI provider",
    },
    loading: "Checking this Gateway for available AI access…",
    testing: "Testing — asking the selected model for a quick reply…",
    retry: "Retry",
    checkAgain: "Check again",
    recovery: {
      unknown:
        "The previous activation is unresolved. Check again refreshes the current setup without repeating it. If a model is available, you can verify and use it.",
      wait: "The previous setup attempt may still be running. Check again can refresh its result. If no model appears, check again after {time} to choose a provider.",
      useCurrent: "Verify & use selected model",
    },
    verify: {},
    access: {
      adminRequired: "Model setup requires operator.admin access.",
      gatewayTooOld: "The Gateway is running an older OpenClaw version",
    },
    candidates: {
      title: "Found on this Gateway",
      recommended: "Recommended",
      credentialsReady: "Credentials ready",
      signInNeeded: "Sign-in needed",
      detected: "Detected",
      testAndUse: "Test & use",
      retry: "Retry test",
      testingButton: "Testing…",
    },
    nativeDiscovery: {},
    nativeModels: {
      title: "Use an installed agent",
      body: "Choose a model from an installed agent. It keeps its own sign-in and tools. Use saves your choice without running the setup test.",
      choose: "Choose a model",
      use: "Use",
      saving: "Saving…",
      loading: "Checking installed agents…",
      empty:
        "No models are available from installed agents. Check the installed app, then open the picker again.",
      unconfirmed: "Availability not confirmed",
    },
    empty: {
      title: "Recommended installs",
      intro: "Install an assistant or local model service, then check again.",
    },
    signIn: {
      title: "Set up and verify a model",
      description:
        "Setup requests a model reply. To save an account first, choose Connect provider.",
      verify: "Set up & verify",
      install: "Review & install",
      custom: "Set up endpoint",
      more: "More sign-in options",
    },
    prepare: {
      title: "Run a model locally",
      intro: "Use a local model service or prepare a private GGUF model on this Gateway.",
      ollamaButton: "Check & set up",
      ollamaLabel: "Ollama",
      ollamaHint: "Connect to the Ollama service on this Gateway and prepare a tools-capable model",
      llamaCppLabel: "llama.cpp",
      providerNotReady:
        "{provider} did not expose a usable local model. Review the setup result, then retry.",
    },
    manual: {
      title: "Connect with an API key or token",
      provider: "Provider and access method",
      selectProvider: "Select a provider",
      selectProviderHint: "Choose where this credential comes from",
      accessValue: "API key or token",
      accessValueFor: "{provider} API key or token",
      accessValuePlaceholder: "Paste an API key or token",
      connectAndVerify: "Connect & verify",
      verifyHint: "OpenClaw verifies a real model reply before marking the connection ready.",
      required: "Choose a provider and enter an API key or token.",
    },
    success: {},
    utility: {},
    failure: {
      auth: "Authentication failed",
      rateLimit: "Rate limited",
      billing: "Billing problem",
      timeout: "Timed out",
      format: "Invalid response",
      unavailable: "Unavailable",
      unknown: "Connection failed",
    },
    failureGuidance: {
      auth: "Review the provider credential or sign-in, then retry.",
      rateLimit: "Wait for the provider limit to reset, then retry.",
      billing: "Restore provider billing or quota, then retry.",
      timeout:
        "The model did not finish the setup test in time. Warm it or choose a faster model, then retry.",
      unavailable: "Make sure the provider service is running and reachable, then retry.",
      format: "Check that the endpoint exposes a compatible chat model, then retry.",
      unknown: "Review the connection details, then retry.",
    },
    errors: {
      requestFailed: "Model setup request failed.",
      activationFailed: "The model could not be activated.",
    },
    wizard: {
      dialogLabel: "Provider sign-in",
      prepareDialogLabel: "Local model setup",
      title: "Sign in with a provider",
      connected: "Account connected. Choose a model when you want to test a reply.",
      prepareTitle: "Set up a local model",
      starting: "Starting provider sign-in…",
      prepareStarting: "Starting local model setup…",
      checking: "Checking your model setup…",
      working: "Working…",
      continue: "Continue",
      submit: "Submit",
      openSignIn: "Open sign-in",
      copy: "Copy",
      copyCode: "Copy code",
      copyLink: "Copy link",
      manualEntry: "Use manual entry",
      browserInstructions: "Complete sign-in in your browser, then return here.",
      waiting: "Waiting for sign-in…",
      deviceCodeWarning:
        "Continue only if you started this sign-in yourself. If a website or another person gave you this code, cancel.",
      expires: "Expires in {count} minutes",
      cancelled: "Provider sign-in was cancelled.",
      finishingStep: "Setup is finishing the current step. You can cancel when it finishes.",
      cancelFailed: "Could not confirm cancellation: {error}",
      sessionExpired:
        "The Gateway no longer has this setup session. It may already have finished. Close this dialog and choose Check again to review the current setup.",
      gatewayNotResponding:
        "The Gateway is not responding. Check that it is running, then try again.",
      gatewayReconnecting: "The Gateway is not responding. Waiting for it to reconnect.",
      notComplete: "Sign-in finished, but model setup is not complete yet.",
    },
  },
  memoryImport: {},
  onboarding: {
    memoryImport: {
      title: "Bring your assistant memory with you",
      body: "OpenClaw found memory from other coding assistants. Import it into your agent workspace?",
      plannedCount: "{count} ready to import",
      alreadyImported: "Already imported: {count}",
      sourceUnavailable: "Source path unavailable",
      import: "Import",
      skip: "Skip",
      reviewDetails: "Review details",
      importingProvider: "Importing…",
      providerResult: "Migrated {migrated}, skipped {skipped}",
      providerIncomplete:
        "Migrated {migrated}, skipped {skipped}, {errors} failed, {conflicts} conflicts",
      providerError: "Import failed: {error}",
      connectionChanged: "Skipped: the Gateway connection changed during the import",
      unknownError: "Request failed",
      doneTitle: "Memory import finished",
      doneBody: "Migrated {migrated}, skipped {skipped}. You can continue setting up OpenClaw.",
    },
  },
  assistantPanel: {
    title: "Assistant sidebar",
    home: "Home",
    toggle: "Talk to your Home agent",
    openHome: "Open Home full page",
    close: "Close assistant sidebar",
    resize: "Resize assistant sidebar",
    dockBottom: "Dock assistant sidebar at bottom",
    dockRight: "Dock assistant sidebar at right",
    context: "Working on: {context}",
    contextOff: "Work context off",
    removeContext: "Remove work context",
    includeContext: "Include work context",
    selection: "Selected text",
    attachSelection: "Attach selected text",
    removeSelection: "Remove selected text",
  },
  custodian: {
    title: "OpenClaw",
    subtitleCaretaker: "System setup and care.",
    exitSetup: "Exit setup",
    newAgent: "New agent",
    hatchDraft: "Wake up, my friend!",
    placeholder: "Message OpenClaw…",
    sensitivePlaceholder: "Enter sensitive value…",
    sensitiveReply: "Sensitive reply sent",
    cancel: "Cancel",
    send: "Send",
    thinking: "OpenClaw is thinking",
    earlier: "Earlier",
    requestFailed: "OpenClaw could not reply. Try again.",
    connectionChanged: "The Gateway connection changed. Retry to continue this setup.",
    sessionRestarted:
      "{error} OpenClaw started a fresh session; earlier messages remain for context.",
    unsupportedGateway: "Update the Gateway to continue setup with OpenClaw.",
    autoSetup: {
      title: "AI setup",
      connecting: "Connecting your AI...",
      verifying: "Checking a real conversation. This may take a minute or more.",
      using: "Using {label} ({modelRef}) on {gateway}",
      sharedCodexLogin: "Using your existing Codex login · sessions shared with Codex",
      separateLogin: "Use a separate login",
      thisComputer: "This computer",
      thisGateway: "this Gateway",
      alternatives: "Or use another AI",
      signInTitle: "Connect ChatGPT to get started",
      signInBody: "OpenClaw is ready to connect your ChatGPT account.",
      signIn: "Sign in with ChatGPT",
      startingSignIn: "Opening ChatGPT sign-in...",
      unavailableTitle: "Your AI needs a little help",
      unavailableBody: "OpenClaw could not connect an AI automatically.",
      nativeSetup: "Open AI setup",
      commandHint: "To connect an AI, run",
      differentGateway: "Use a different Gateway",
      reviewPermissions: "Review permissions",
      failed: "OpenClaw could not finish AI setup. Try again.",
      safety:
        "OpenClaw can take actions using the permissions and services you enable. Review prompts and only connect tools you trust.",
    },
    panel: {
      title: "OpenClaw",
      toggle: "Toggle Ask OpenClaw",
      close: "Close Ask OpenClaw",
      resize: "Resize Ask OpenClaw",
      dockBottom: "Dock Ask OpenClaw at bottom",
      dockRight: "Dock Ask OpenClaw at right",
    },
    history: {
      button: "History",
      title: "Recent changes",
      description: "What changed on this system, newest first.",
      loading: "Loading recent changes…",
      empty: "No recorded changes yet.",
      requestFailed: "OpenClaw could not load change history.",
      loadMore: "Load more",
      loadingMore: "Loading more…",
      changedPaths: "Changed paths ({count})",
      invalidEdit: "This manual edit did not pass configuration validation.",
      opaqueChange: "Formatting or comments changed without visible configuration path changes.",
      sources: {
        systemAgent: "system-agent",
        doctor: "doctor",
        settings: "settings",
        cli: "CLI",
        pluginInstall: "plugin install",
        manualEdit: "manual edit",
        unknown: "unknown",
      },
    },
    nudge: {
      configReload: "Configuration reload stopped — ask me what happened",
      channelAuth: "{channel} authentication degraded — ask me what happened",
      channelDisconnected: "{channel} just disconnected — ask me what happened",
      channelDegraded: "{channel} is degraded — ask me what happened",
      channelFallback: "A channel",
      dismiss: "Dismiss this update",
      channelSetupTitle: "Reach OpenClaw outside this app",
      channelSetupBody:
        "The web app already works. Add a channel only if you want to message OpenClaw from another service.",
      channelSetupAction: "Set up a channel",
      channelSetupDismiss: "Keep using the web app",
      channelStatusErrorTitle: "Channel status is unavailable",
      channelStatusErrorBody: "Retry the check, or keep using the web app without a channel.",
    },
  },
  mcpServers: {},
  mcpPage: {},
  talkPage: {},
  memoryPage: {},
  sessionsPage: {
    hubTablistLabel: "Session sections",
  },
  pluginsPage: {},
  labsPage: {
    intro:
      "Labs contains experimental capabilities that may change, break, or disappear between releases.",
    sectionTitle: "Experimental features",
    sectionDescription:
      "Changes save without restarting the Gateway. Code Mode and Tool Search apply to future agent runs.",
    documentation: "Documentation",
    restartRequired: "Gateway restart required.",
    saveErrorTitle: "Could not update feature",
    saveFailed: "The feature setting could not be saved.",
    decisionAssistance: {},
    codeMode: {},

    swarm: {
      title: "Swarm",
      groupTitle: "Parallel tasks",
      defaultPhase: "Unphased",
      progress: "{complete} of {total}",
      active: "{running} running · {queued} queued · {failed} failed or stopped",
      finished: "{done} completed · {failed} failed or stopped",
      completed: "{done} completed",
      failedOrStopped: "Failed or stopped",
      childOutcome: "Child runs finished. Check the conversation for the final response.",
      childOutcomeProcessing: "Child runs finished. The parent is processing their results.",
      details: "Child details",
      detailsUnavailable: "Child details are unavailable. Counts include all accepted workers.",
      otherGroups: "{count} more active groups",
    },
    toolSearch: {},

    customPluginUi: {},

    hostDesktop: {},
    workerDesktop: {},
  },
  aboutPage: {
    productName: "OpenClaw",
    tagline: "Your personal AI assistant, running on your own devices.",
    waveHello: "Wave hello to Clawd",
    linksLabel: "Community and resources",
    linkWebsite: "Website",
    linkDocs: "Docs",
    linkGitHub: "GitHub",
    linkDiscord: "Discord",
    linkX: "X (Twitter)",
    linkChangelog: "Changelog",
    license: "© 2026 OpenClaw Foundation — MIT License.",
    artifactTitle: "Control UI",
    artifactSubtitle: "Identity embedded when this browser artifact was built.",
    artifactDetails: "Control UI build details",
    version: "Version",
    commit: "Commit",
    branch: "Branch",
    built: "Built",
    unavailable: "Unavailable",
    copyCommit: "Copy full commit hash",
    copyingCommit: "Copying commit hash",
    copiedCommit: "Commit hash copied",
    copyCommitFailed: "Could not copy commit hash",
    gateway: "Gateway",
    gatewayVersion: "Connected Gateway version",
    gatewayVersionHint:
      "Reported by the active Gateway connection; separate from this Control UI build.",
  },
  appsPage: {},
  presence: {
    sharedOwner: {
      name: "Shared owner",
      hint: "Connected with the Gateway token or over a tunnel, not a personal sign-in.",
    },
    rosterTitle: "Online",
    filters: {
      label: "Filter & sort people",
      noMatches: "No people match this filter",
      presence: "Active people first",
      reset: "Reset to defaults",
      running: "Running sessions",
      total: "Total sessions",
      name: "Name",
    },
    active: "Active",
    onlineActive: "Online · Active",
    onlineIdle: "Online · Idle",
    idle: "Idle",
    offline: "Offline",
    sessions: {
      openCount: "{count}\u00a0open",
      runningCount: "{count}\u00a0running",
      openHint:
        "Owned, unarchived conversations you can access across agents. Excludes hidden subagents, automation, and system sessions.",
      runningHint: "Open sessions actively executing an agent turn. Queued work is not running.",
      counts: "{open} open sessions, {running} running",
      unavailable: "Session counts unavailable",
      retry: "Counts may be out of date. Retry",
    },
    card: {
      details: "Details for {name}",
      loadFailed: "Could not open details. Try again, or open this person’s Activity page.",
      ariaLabel: "Activity for {name}",
      person: "Person",
      onlineFor: "Online for",
      where: "Where",
      reportedTimeZone: "Reported time zone: {zone}",
      lastActivity: "Last interaction",
      notObserved: "Activity unavailable",
      viewingNow: "Viewing now",
      recentSessions: "Recent sessions",
      noVisibleSessions: "No visible sessions being viewed.",
      noRecentSessions: "No recent visible sessions.",
      sessionUpdated: "Session updated",
      ago: "ago",
      viewActivity: "View activity",
      web: "Web app",
      cli: "Command line",
      terminal: "Terminal",
      app: "App",
    },
  },
  activityFeed: {
    partialHistory:
      "People and counts describe visible recorded session associations; retained history and this people list may be incomplete.",
    sessionsMode: "Sessions",
    searchPlaceholder: "Search session titles…",
    time: "Time",
    time24h: "Last 24 hours",
    time7d: "Last 7 days",
    time30d: "Last 30 days",
    timeAll: "All time",
    everyone: "Everyone",
    peopleButtonLabel: "Filter sessions by person",
    lastActive: "· {time}",
    unresolvedIdentities: "Unresolved identities",
    clearPersonFilter: "Clear person filter",
    showing: "Showing {shown} of {total}",
    today: "Today",
    yesterday: "Yesterday",
    unknownDate: "Unknown date",
    noSessions: "No sessions match these filters.",
    automationGroup: "{count} automation sessions",
    automation: "Automation",
    inspectRun: "Inspect run",
    recap: "Session recap",
    recapMissing: "No recap yet",
    recapUpdating: "Updating recap…",
    recapStale: "New activity since this recap",
    recapUnavailable: "Recap unavailable",
    recapRefreshFailed: "Couldn’t refresh recap",
    recapRetry: "Retry recap",
    recapUpdated: "Recap updated {time}",
    backToSessions: "Back to sessions",
    channelLabel: "Channel: {value}",
    agentLabel: "Agent: {value}",
    online: "Online",
    idle: "Idle",
    offline: "Offline",
    unknownConnection: "Unknown client",
    connectionDetails: "Connection details · {count}",
    connectionOne: "1 connection",
    connectionMany: "{count} connections",
    lastInput: "Last input {time} ago",
    viewingNow: "Viewing now",
    notViewing: "Not viewing a session right now.",
    notFoundTitle: "Person not found",
    notFoundDescription: "No online presence or visible session association matches this identity.",
  },
  githubPublication: {
    newAction: "Choose a new publication",
    capacity:
      'Too many publications are awaiting review. Finish an existing publication, then select "{newAction}" to make room. Existing retries remain available.',
  },
  githubConnections: {
    title: "GitHub connections",
    mine: "My GitHub",
    system: "System GitHub",
    forMe: "For me",
    forSystem: "For the system",
  },
  profilePage: {
    personalInstructions: {
      title: "Personal instructions",
      description:
        "Tell this agent about you and how you like to work. This edits only your personal USER.md, not the shared workspace file.",
    },
    offline: "Connect to the gateway to meet your agent.",
    access: {},
    usageStatistics: "Usage statistics",
    usageStatisticsDescription: "View activity, costs, and usage trends.",
    identity: {
      title: "Identity",
      menuLabel: "Identity menu",
      menuButtonLabel: "Identity and app menu for {name}",
      description: "Your profile on this gateway.",
      avatar: "Avatar",
      chooseAvatar: "Choose image",
      displayName: "Display name",
      linkedEmails: "Linked emails",
    },
    modelAccounts: {},
  },
  skillWorkshop: {},
  // Chat swarm summaries render before the lazy Activity catalog loads.
  // Keep their shared label in startup English; Activity replaces the full namespace on entry.
  activity: {
    status: {
      done: "Done",
    },
  },
  gatewayLogs: {
    title: "Logs",
    subtitle: "Gateway file logs (JSONL).",
    exportButton: "Export {label}",
    exportLabels: {
      filtered: "filtered",
      visible: "visible",
    },
    filter: "Filter",
    searchPlaceholder: "Search logs",
    autoFollow: "Auto-follow",
    file: "File: {file}",
    truncated: "Log output truncated; showing latest chunk.",
    empty: "No log entries.",
  },
  pluginTabs: {
    unavailableTitle: "Plugin panel unavailable",
    unavailableSubtitle:
      "The plugin that owns this tab is not active on the connected gateway, or it did not provide a panel.",
  },
  logbook: {
    nav: {
      previousDay: "Previous day",
      nextDay: "Next day",
      today: "Today",
    },
    status: {
      capturing: "Capturing every {seconds}s",
      paused: "Capture paused",
      disabled: "Capture off",
      nodeHelp: "Device providing screen snapshots.",
      pending: "{count} frames queued",
      pendingHelp: "Snapshots waiting for the next analysis batch.",
      analyzing: "Analyzing…",
      captureError: "Capture error",
      batchError: "Analysis error",
      modelMissing: "No vision model",
      modelMissingHelp:
        "Set plugins.entries.logbook.config.visionModel or configure tools.media models.",
    },
    actions: {
      pause: "Pause",
      resume: "Resume",
      analyzeNow: "Analyze now",
    },
    empty: {
      title: "Nothing on the timeline yet.",
      subtitle:
        "Logbook is collecting snapshots; cards appear after the first analysis batch completes.",
    },
    card: {
      keyframeAlt: "Screen snapshot from this activity",
      distractions: "Distractions",
    },
    stats: {
      title: "Day at a glance",
      focus: "{pct}% focus",
      tracked: "{duration} tracked",
    },
    standup: {
      title: "Daily standup",
      generate: "Generate",
      refresh: "Regenerate",
      empty: "Turn today's timeline into a ready-to-paste standup update.",
    },
    ask: {
      title: "Ask your day",
      placeholder: "When did I review the gateway PR?",
      submit: "Ask",
    },
  },
  board: {
    label: "Session dashboard",
    tabsLabel: "Dashboard tabs",
    moreTabs: "More dashboard tabs",
    gridLabel: "Dashboard widgets",
    emptyTitle: "A clear board, ready for work",
    emptyHint: "Your agent can pin widgets here — try asking for a status card.",
    actionFailed: "The dashboard change could not be saved.",
    actionInProgress: "Another dashboard change is still being saved.",
    announcement: {
      moved: "Moved {title}.",
      resized: "Resized {title}.",
      removed: "Removed {title}.",
      granted: "Widget access allowed.",
      rejected: "Widget access rejected.",
    },
    widget: {
      cellLabel:
        "Dashboard widget: {title}. Use arrow keys to navigate. Hold Alt and press an arrow key to move it.",
      moveHandle: "Move {title}",
      resizeHandle: "Resize {title}",
      menuLabel: "Widget options",
      moveToTab: "Move to tab",
      noOtherTabs: "No other tabs",
      resize: "Resize",
      autoHeight: "Auto height",
      remove: "Delete",
      needsApproval: "Needs approval",
      needsApprovalDetail: "This widget requested additional access.",
      networkAccess: "Network origins",
      hostTools: "Host tools and data",
      activeCapabilities: "Active widget capabilities",
      networkCapability: "Network: {capability}",
      toolCapability: "Tool: {capability}",
      granted: "Granted",
      allow: "Allow",
      allowFailed: "Could not allow widget access. Try again.",
      reject: "Reject",
      rejectFailed: "Could not reject widget access. Try again.",
      rejected: "Access rejected",
      rejectedDetail: "This widget stays inactive until it is removed or replaced.",
      appLoading: "Restoring app…",
      appStaleTitle: "This pinned app is stale",
      appStaleDetail: "Its server, resource, or originating transcript is no longer available.",
      retry: "Retry",
      frameResolverMissing: "Widget content is unavailable.",
      sandboxUnavailable: "Widget sandbox host is unavailable.",
      runtimeError: "Script error: {message}",
      waitingForConnection: "Waiting for the connection. This view will recover automatically.",
      resourceUnavailable:
        "A widget resource could not load. Retry when the connection is available.",
      frameAuthorizationFailed: "Widget authorization failed after repeated refresh attempts.",
      sandboxOriginRequired:
        "Widget authorization failed after repeated refresh attempts. If the gateway runs behind a reverse proxy or tunnel that does not route the widget sandbox port, set mcp.apps.sandboxOrigin to a dedicated public origin routed to the sandbox listener.",
      errorTitle: "This widget could not load",
      errorDetail: "The problem is contained to this card.",
      actionErrorTitle: "Widget change failed",
      actionErrorDetail: "The dashboard kept the previous widget state.",
      errorShow: "Show details",
      kindMcp: "MCP",
      kindHtml: "HTML",
      kindPlugin: "Plugin",
      kindReport: "Report",
      kindWebsite: "Website",
      kindBrowserDashboard: "Browser dashboard",
      pluginLoading: "Loading plugin widget…",
      disabledPlugin: "Widget from disabled plugin {pluginId}",
    },
  },
  connection: {
    browserSignIn: {
      title: "Sign in to continue loading content",
      description:
        "Your website sign-in has expired. Images and files cannot load until you sign in again. Your conversation and draft will stay open.",
      action: "Sign in",
      dismiss: "Not now",
      returnHint:
        "Finish signing in in the new tab, then return here. Visible attachments will retry automatically.",
      checkAgain: "Check again",
      checking: "Checking access…",
      stillRequired: "Sign-in is still required. Finish signing in, then check again.",
      unavailable: "Could not verify access. Check your connection and try again.",
    },
    disconnectedTitle: "Disconnected",
    connecting: "Connecting…",
    starting: "Starting…",
    restoring: "Restoring…",
    "reload-required": "Refresh required",
    offline: "Disconnected",
    reconnecting: "Reconnecting…",
    interruptedRetrying:
      "Connection to the Gateway was interrupted. Reconnecting automatically. (WebSocket {code})",
    interrupted:
      "Connection to the Gateway was interrupted. Check your connection and try again. (WebSocket {code})",
    restarting: "Restarting…",
    suspending: "Suspending…",
    suspended: "Suspended",
    retryNow: "Retry now",
    actionsUnavailable: "Actions are unavailable while the Gateway reconnects.",
    settingsChangesUnavailable:
      "Changes to settings are disabled while the Gateway is reconnecting.",
    sessionMoveRefreshFailed: "The session move was saved, but refreshing the list failed: {error}",
    sessionMoveUncertain:
      "The session move could not be confirmed. Refresh and check its group before retrying. {error}",
    sessionOperationCompletedPreviousConnection:
      "The session operation completed on the previous connection. Check the current session list before continuing.",
    sessionOperationCompletedPreviousConnectionWithRefreshError:
      "The session operation completed on the previous connection, but refreshing the current session list failed: {error}",
    scopeUpgrade: {},
    // Settings → Gateway copy lives in the lazy en-settings catalog; the anchor keeps its merge target.
    access: {},
    switchGateway: {
      title: "Switch to a different Gateway?",
      summary: "This link asks the browser to connect somewhere else.",
      current: "Current",
      next: "Link",
      note: "Anything you enter after switching goes to that host.",
      noteToken: "The link also carries a token for it.",
      noteScoped: "Your saved credential for {host} stays here and is not sent.",
      confirm: "Switch to {host}",
      cancel: "Keep current Gateway",
    },
    help: {
      title: "How to connect",
      step1: "Start the gateway on your host machine:",
      step2: "Get a tokenized dashboard URL:",
      step3: "Paste the Gateway URL and secret above, or open that URL directly.",
      docsLink: "Read the docs →",
      copyCommand: "Copy command",
      copyCommandAria: "Copy command: {command}",
    },
  },
  // The Inbox loader renders before its diagnostics catalog loads.
  attention: {
    issues: "Inbox",
  },
  standingGrants: {
    title: "Standing grants",
    description:
      "Allow-always on an automation approval mints a standing grant for that exact command. Revoking one makes the next occurrence ask again.",
    historyTitle: "Approval history",
    empty: "No standing grants. Answer an automation approval with Always allow to mint one.",
    stateUntilRevoked: "Until revoked",
    stateExpiresIn: "Expires in {count}d",
    stateExpired: "Expired",
    stateRevoked: "Revoked",
    revoke: "Revoke",
    revoking: "Revoking…",
    columns: {
      automation: "Automation",
      command: "Command",
      uses: "Uses",
      state: "State",
    },
  },
  approvalHistory: {
    description:
      "Terminal exec, plugin, and system-agent approvals recorded by this gateway, newest first.",
    loading: "Loading approval history…",
    loadingMore: "Loading…",
    loadMore: "Load more",
    empty: "No resolved approvals in the rolling 30-day window.",
    retention: "Approval history is a rolling 30-day window.",
    offline: "Connect to the gateway to load approval history.",
    invalidResponse: "The gateway returned an invalid approval history response.",
    unknown: "Unknown",
    notApplicable: "N/A",
    columns: {
      resolved: "Resolved",
      kind: "Kind",
      request: "Request",
      decision: "Decision",
      reason: "Resolution reason",
      source: "Source agent / session",
      resolver: "Resolver",
    },
    kinds: {
      exec: "Exec",
      plugin: "Plugin",
      systemAgent: "System agent",
    },
    statuses: {
      allowed: "Allowed",
      denied: "Denied",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    decisions: {
      allowOnce: "Allow once",
      allowAlways: "Always allow",
      deny: "Deny",
    },
    reasons: {
      user: "User decision",
      timeout: "Timed out",
      malformedVerdict: "Malformed verdict",
      noRoute: "No route",
      runAborted: "Run aborted",
      gatewayRestart: "Gateway restart",
      storageCorrupt: "Storage corrupt",
    },
  },
  commandPalette: {},
  palette: {
    placeholder: "Search or start a task…",
    categories: { navigation: "Navigation" },
  },
  dreaming: {},
  modelProviders: {},
  searchPage: {},
  usage: {
    common: {
      emptyValue: "—",
      unknown: "unknown",
    },
    loading: {
      title: "Usage Overview",
      badge: "Loading",
    },
    metrics: {
      tokens: "Tokens",
      cost: "Cost",
      session: "session",
      sessions: "sessions",
    },
    heatmap: {
      title: "Token Activity",
      subtitle: "Daily token intensity for the selected range, up to one year.",
      cellTokens: "{tokens} tokens",
      less: "Less",
      more: "More",
    },
    providerUsage: {
      title: "Provider plans & billing",
      subtitle: "Live plan, quota, balance, and budget data reported by configured providers.",
      stalled: "Provider usage did not finish loading. Refresh to retry.",
      unavailable: "Provider usage is unavailable; the last request failed. Refresh to retry.",
      balance: "Balance",
      spend: "Usage",
      budget: "Budget",
      today: "Today",
      last7Days: "7 days",
      lastDays: "{count} days",
      dailyCost: "Daily provider cost",
      requests: "{count} requests",
      inputTokens: "{count} input",
      cacheTokens: "{count} cache",
      outputTokens: "{count} output",
      topModels: "Top models",
      costCategories: "Cost categories",
      remaining: "{percent}% left",
      resets: "Resets {date}",
    },
    presets: {},
    scope: {},
    filters: {},
    query: {},
    export: {},
    cacheStatus: {},
    empty: {},
    daily: {},
    costWindows: {},
    breakdown: {
      output: "Output",
      input: "Input",
      cacheWrite: "Cache Write",
      cacheRead: "Cache Read",
      total: "Total",
      tokensByType: "Tokens by Type",
      costByType: "Cost by Type",
    },
    overview: {
      title: "Usage Overview",
    },
    sessions: {},
    details: {
      noUsageData: "No usage data for this session.",
      duration: "Duration",
      modelMix: "Model Mix",
      filtered: "(filtered)",
      close: "Close session details",
      noTimeline: "No timeline data",
      loadFailed: "Could not load {detail}: {error}",
      noDataInRange: "No data in range",
      usageOverTime: "Usage Over Time",
      rangeStart: "Range start",
      rangeEnd: "Range end",
      reset: "Reset",
      perTurn: "Per Turn",
      cumulative: "Cumulative",
      turnRange: "Turns {start}–{end} of {total}",
      assistantOutputTokens: "Assistant output tokens",
      userToolInputTokens: "User + tool input tokens",
      tokensWrittenToCache: "Tokens written to cache",
      tokensReadFromCache: "Tokens read from cache",
      noContextData: "No context data",
      contextOutOfDate: "These context details are out of date. Refresh usage and try again.",
      systemPromptBreakdown: "System Prompt Breakdown",
      collapse: "Collapse",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      baseContextPerMessage: "Base context per message",
      system: "System",
      systemShort: "Sys",
      skills: "Skills",
      tools: "Tools",
      files: "Files",
      ofInput: "of input",
      of: "of",
      timelineFiltered: "timeline filtered",
      loadedIntervalMessages: "Loaded conversation · selected interval",
      conversation: "Conversation",
      noMessages: "No messages",
      tool: "Tool",
      toolResult: "Tool result",
      hasTools: "Has tools",
      searchConversation: "Search conversation",
      filterByRole: "Filter by role",
      filterByTool: "Filter by tool",
      you: "You",
      noMessagesMatch: "No messages match the filters.",
    },
    mosaic: {},
  },
  // Login copy lives in the lazy en-login catalog; the anchor keeps its merge target.
  login: {},
  chat: {
    sessionDetails: {
      title: "Details",
      close: "Close details",
      session: "Session details",
      createdBy: "Created by",
      owner: "Owner",
      participants: "{count} participants",
      moreParticipants: "{count} more participants",
      workspaceUnavailable: "Workspace unavailable",
      allChanges: "All changes",
      pullRequests: "Pull requests",
      noPullRequests: "No pull requests",
      automations: "Automations",
      progressOptions: "Task progress options",
      hideProgress: "Don't show task progress again",
      collapseDefault: "Collapse task progress by default",
      settings: "Task progress settings",
      progressHidden: "Task progress hidden in this browser.",
      automationError: "Couldn't load automations.",
      automationUnavailable: "Automations unavailable for this session.",
      automationOffline: "Offline · last-known automations",
      automationLoading: "Loading automations…",
      automationEmpty: "No automations for this session",
      automationMore: "Load more automations",
      automationAttention: "Needs attention",
      automationPaused: "Paused",
      automationEnabled: "Enabled",
    },
    agentDatabaseWarming: "This agent is still starting. Retry in a moment.",
    historyRequestTimedOut: "Chat history request timed out. Retry to load the conversation.",
    clawhub: {
      retryStatus: "Status unavailable · Retry",
      viewDetails: "View details",
    },
    modelAccounts: {
      section: "Account",
      automatic: "Automatic (new-chat default)",
      manage: "Manage saved accounts…",
    },
    mentions: {
      menu: "Mention a person",
      empty: "No people found",
      truncated: "Keep typing to find more people.",
      online: "Online",
      offline: "Offline",
      unavailable: "Could not load people. Try again.",
      limit: "You can mention up to 10 people per message.",
      selectedLabel: "Will notify",
      selected: "Will notify: {names}",
      remove: "Remove mention",
      restoreFailed:
        "Mention selection could not be restored. Edit this message and select its recipients again.",
      unsupported:
        "Human mentions are not available in this mode. Remove the selected mentions or send from a normal chat.",
    },
    cloudWorkerFailed: "Runner failed: {error}",
    errorDetails: "Error details",
    errorReplySummary: "Couldn't finish this reply. Check the conversation before trying again.",
    recovery: {
      retrying: "Retrying…",
      retryingIn: "Retrying in {seconds}s",
      tryingFallback: "Trying fallback model…",
      failedTitle: "Unable to finish this reply",
      failedHelp: "Automatic recovery has stopped. Check any completed work before trying again.",
      tryAgain: "Try again",
      changeModel: "Change model",
      continuePrompt:
        "Continue from where you stopped. Check which actions already completed before retrying anything.",
    },
    errorBusySummary: "OpenClaw is busy. Check status before trying again.",
    errorSignInSummary: "Couldn't sign in to the AI service. Sign in again under Models.",
    errorStartSummary: "Couldn't start this conversation. Open Settings → Logs for details.",
    checkStatus: "Check status",
    details: "Details",
    copyError: "Copy error",
    providerAccessRemoved:
      "This reply stopped because the provider was signed out. Sign in again or choose another model.",
    providerReview: {},
    providerPolicy: {
      bufferingTitle: "Cyber safety review",
      bufferingBody: "OpenAI is reviewing this response for cyber safety.",
      blockedTitle: "Cyber policy block",
      blockedBody: "OpenAI blocked this response under its cyber policy.",
      fallbackTitle: "Provider changed the model",
      fallbackBody: "OpenAI routed this response to {model}.",
      fallbackUnknownBody: "OpenAI routed this response to another model.",
      escalatedTitle: "Retried on Daybreak",
      escalatedBody: "OpenAI declined this request; retried on {model}.",
      unavailableTitle: "Daybreak unavailable",
      unavailableBody: "OpenAI declined this request; {model} is not authorized.",
    },
    diskSpace: {
      warningTitle: "Cloud session disk space is low",
      criticalTitle: "Cloud session disk space is critically low",
      warningBody:
        "{percent}% used · {free} free. Delete unneeded files or stop the cloud worker before large writes.",
      criticalBody:
        "{percent}% used · {free} free. New writes may fail and stop the agent. Delete unneeded files or stop the cloud worker before large writes.",
    },
    workerRuntimeInstall: {
      transferringTitle: "Updating worker runtime · {transferred} of {total} ({percent}%)",
      installingTitle: "Installing worker runtime",
      transferringBody:
        "Transferring the new worker runtime to this device: {transferred} of {total} ({percent}%). The next turn starts when it finishes.",
      installingBody: "Installing the new worker runtime on this device.",
    },
    sendErrors: {
      outboxPayloadCopied:
        "This queued message was copied from another tab. Check the conversation and retry only if it has not arrived.",
      outboxPayloadCapacity:
        "Browser attachment storage is full. Try a smaller batch or send/discard queued messages to free space. No new message was sent; your input is retained.",
      outboxPayloadUnavailable:
        "Browser attachment storage is unavailable. Allow browser storage and close older dashboard tabs before reconnecting and retrying. No new message was sent.",
      outboxPayloadMissing:
        "Queued attachments are missing or unreadable. This may be a stale copy from another tab. Check the conversation, then discard this row and attach the files again if needed. No new message was sent.",
      activeLeafChanged: "The session switched branches — review and resend.",
    },
    waitingForApproval: "Waiting for approval…",
    waitingOnSubagents: "Waiting on subagents",
    turnElapsed: "Total time since your message, including subagent waits",
    waitingOnSubagent: "Waiting on {name}",
    subagentsRunning: "{count} subagents running",
    subagentsRunningOne: "1 subagent running",
    startupStatus: {
      retrying: "Retrying… {attempt}/{maxAttempts}",
      preparingWorkspace: "Preparing workspace…",
      namingWorktree: "Naming worktree…",
      creatingWorktree: "Creating worktree…",
      runningSetup: "Running setup…",
      provisioningEnvironment: "Provisioning environment…",
      startingFromSnapshot: "Starting from snapshot…",
      preparingCloudEnvironment: "Preparing cloud environment…",
      savingSnapshot: "Saving snapshot…",
      preparingContext: "Preparing this turn…",
      memoryFlushing: "Saving conversation memory…",
      startingModel: "Waiting for a response…",
      waitingForState: "Temporarily busy—retrying…",
    },
    archivedSessionDisabled: "This session is archived. Unarchive it to continue the conversation.",
    subagentViewOnly: "View-only subagent",
    subagentSessionDisabled: "Continue in {parent}.",
    processesPanel: { title: "Processes", refresh: "Refresh processes" },
    subagentsPanel: {
      title: "Subagents",
      back: "Back to Subagents",
      running: "Running ({count})",
      finished: "Finished ({count})",
      callsOne: "{count} call",
      callsMany: "{count} calls",
      callsAtLeast: "{count}+ calls",
      empty: "No subagents in this conversation.",
      noRunning: "No running subagents",
      refresh: "Refresh subagents",
      stop: "Stop {name}",
      stopping: "Stopping…",
      elapsed: "Elapsed time",
      duration: "Run duration",
    },
    parentSession: "the parent session",
    openParentSession: "Open parent session",
    parentSessionUnavailable: "Parent session information is unavailable.",
    sessionRoute: {
      chooseTitle: "Choose a session",
      multipleMatches: "More than one session matches {shortId}.",
      additionalMatches: "Search results remain. Use a longer id prefix.",
      notFoundTitle: "Session not found",
      notFoundExplanation: "The session may have been removed, or the link may be incorrect.",
      goToMain: "Go to main session",
      viewSessions: "View sessions",
      catalogShareUnavailable: "This shared session route is unavailable.",
      catalogShareInvalid: "This {catalog} share URL is invalid.",
    },
    commandResults: {
      startingNewThread: "Starting new session...",
      resettingThread: "Resetting session...",
      stoppingCurrentRun: "Stopping current run...",
      chatHistoryCleared: "Chat history cleared.",
      emptyExport: "There are no messages to export yet.",
      exportPathUnsupported:
        "Control UI exports Markdown through your browser. Run /export without a file path.",
      unknownCommand: "Unknown command: `{command}`",
      options: "Options: {options}.",
      sessionUnavailable: "Session capability is unavailable",
      help: {
        availableCommands: "Available Commands",
        agentCommand: "agent",
        openMenu: "Type `/` to open the command menu.",
      },
      compaction: {
        failed: "Compaction failed.",
        failedWithReason: "Compaction failed: {reason}",
        tokenSummary: " ({before} -> {after} tokens)",
        succeeded: "Context compacted successfully",
        skipped: "Compaction skipped.",
        skippedWithReason: "Compaction skipped: {reason}",
      },
      model: {
        current: "**Current model:** {model}",
        available: "**Available:** {models}",
        more: " +{count} more",
        getFailed: "Failed to get model info: {error}",
        set: "Model set to {model}.",
        setFailed: "Failed to set model: {error}",
      },
      thinking: {
        current: "Current thinking level: {level}.",
        getFailed: "Failed to get thinking level: {error}",
        reset: "Thinking level reset to default.",
        resetFailed: "Failed to reset thinking level: {error}",
        unrecognized: 'Unrecognized thinking level "{level}". Valid levels: {options}.',
        unsupported:
          'Unsupported thinking level "{level}" for this model. Valid levels: {options}.',
        set: "Thinking level set to {level}.",
        setFailed: "Failed to set thinking level: {error}",
      },
      verbose: {
        current: "Current verbose level: {level}.",
        getFailed: "Failed to get verbose level: {error}",
        unrecognized: 'Unrecognized verbose level "{level}". Valid levels: off, on, full.',
        set: "Verbose mode set to {level}.",
        setFailed: "Failed to set verbose mode: {error}",
      },
      fast: {
        autoValue: "auto ({seconds} sec)",
        on: "on",
        off: "off",
        sourceSession: " (session)",
        sourceAgent: " (default: agent)",
        sourceModel: " (default: model)",
        sourceDefault: " (default)",
        current: "Current fast mode: {value}",
        options: "on, off, auto ({seconds} sec), default, status",
        getFailed: "Failed to get fast mode: {error}",
        reset: "Fast mode reset to default.",
        resetFailed: "Failed to reset fast mode: {error}",
        unrecognized:
          'Unrecognized fast mode "{mode}". Valid levels: on, off, auto, default, status.',
        setAuto: "Fast mode set to auto.",
        enabled: "Fast mode enabled.",
        disabled: "Fast mode disabled.",
        setFailed: "Failed to set fast mode: {error}",
      },
      usage: {
        noActiveThread: "No active session.",
        notAvailable: "n/a",
        title: "Session Usage",
        inputTokens: "Input: {count} tokens",
        outputTokens: "Output: {count} tokens",
        totalTokens: "Total: {count} tokens",
        context: "Context: {percent} of {total}",
        promptBudget: "Prompt budget (last run): {percent} of {total}",
        model: "Model: {model}",
        failed: "Failed to get usage: {error}",
      },
      agents: {
        none: "No agents configured.",
        title: "**Agents** ({count})",
        default: "default",
        runtime: " · runtime {runtime}",
        failed: "Failed to list agents: {error}",
      },
      steer: {
        timeout: "The active run ended before the steer message was accepted.",
        failed: "Steer failed before it reached the run; try again.",
        usage: "Usage: `/steer <message>`",
        succeeded: "Steered.",
        requestFailed: "Failed to steer: {error}",
      },
      redirect: {
        timeout: "The active run ended before the redirect message was accepted.",
        failed: "Redirect failed before it reached the run; try again.",
        usage: "Usage: `/redirect <message>`",
        succeeded: "Redirected.",
        requestFailed: "Failed to redirect: {error}",
      },
    },
    sessionSharing: {
      menu: "Session sharing",
      share: "Share",
      current: "Session visibility: {visibility}",
      visibility: "Visibility",
      shared: "Shared",
      readOnly: "Read-only",
      suggest: "Suggest",
      draft: "Draft",
      publishDraft: "Publish draft",
      owner: "Owner",
      members: "Members",
      selected: "Member",
      noPeople: "No paired people found.",
      readOnlyNotice: "Only the session owner and members can act in this session.",
      scopeReadOnlyNotice: "Sending messages is unavailable with your current access.",
      publicAccess: "Public access",
      publicIndicator: "Public",
      worldReadable: "Public — anyone can read without signing in.",
      notPublic: "Only signed-in people with access can view this session.",
      enablePublicAccess: "Enable public access…",
      disablePublicAccess: "Disable public access",
      copyPublicLink: "Copy public link",
      publicConfirmTitle: "Make this session world-readable?",
      publicConfirmMessage:
        "Anyone with the public link can read existing and future conversation text, including all user and assistant messages and progress, plus tool names, short argument summaries, and images without signing in.\n\nReasoning, tool-result text, and private dashboards stay hidden. Image contents are not redacted. Review for private information before sharing; disabling access cannot recall downloaded copies.",
      publicConfirmEnable: "Make public",
      publicEnabled: "Public access enabled. Copy the public link from Session sharing.",
      publicDisabled: "Public access disabled.",
      publicUnavailable: "Public access requires a saved, non-incognito session.",
    },
    reactions: {
      add: "Add reaction",
      quick: "Quick reactions",
      emoji: "Emoji",
      more: "More…",
      back: "Back to quick reactions",
      placeholder: "Any emoji",
      shortcut: "{shortcut} opens your emoji picker.",
      hint: "Type or paste an emoji.",
      invalid: "Reactions are a single emoji.",
      you: "You",
      andOthers: "{names} and {count} others",
      reactedWith: "{names} reacted with {emoji}",
    },
    sessionSuggestions: {
      suggest: "Suggest",
      suggestMessage: "Suggest message",
      attachmentsUnsupported: "Remove attachments before submitting a text suggestion.",
      sendNow: "Send {author}'s suggestion now",
      queue: "Queue {author}'s suggestion",
      edit: "Edit {author}'s suggestion",
      dismiss: "Dismiss {author}'s suggestion",
      typing: "{name} is typing…",
      typingMany: "{names} are typing…",
      typingSeveral: "Several people are typing…",
      typingOthers: "{count} others",
      otherCollaborators: "Other collaborators",
      typingDraftState: "is typing...",
      pausedDraftState: "Draft",
      state: {
        pending: "Pending",
        accepted: "Accepted",
        dismissed: "Dismissed",
      },
    },
    sessionHeader: {
      renameTooltip: "Rename session",
      renameAria: "Rename session {title}",
      renameInputAria: "Session title",
      renameInputPlaceholder: "Session title",
      openParent: "Open parent session {title}",
      subagentsRunning: "Subagents · {count} running",
      layout: "Layout",
      panels: "Panels",
      continueInTerminal: {
        action: "Continue in terminal…",
        title: "Continue in terminal",
        description:
          "Copy this command to continue the current session. It is safe to paste in common terminals and shells.",
        authNote:
          "The command contains no credentials. The terminal authenticates independently, and the session's access controls still apply.",
        disconnected: "Connect to the Gateway to continue this session in a terminal.",
        queryRouted:
          "Query-routed Gateway URLs cannot create credential-free continuation commands because authentication and stored device scope are not query-aware. Use a manually authenticated CLI target or a queryless configured Gateway URL.",
        unavailable: "This session or Gateway address cannot be continued in a terminal.",
      },
      workspaceAria: "Workspace actions for {workspace}",
      revealFinder: "Reveal in Finder",
      revealFileExplorer: "Reveal in File Explorer",
      revealFileManager: "Open in file manager",
      copyPath: "Copy path",
      copyBranch: "Copy branch name",
      copied: "Copied",
      branches: "Versions",
      versionsHelpLabel: "About versions",
      versionsHelp:
        "Saved copies of this conversation from rewinds or repairs. You can return to an earlier version here.",
      branchSwitchUnavailable: "Versions cannot be switched while the agent is working.",
      untitledBranch: "Untitled version",
      oneMessage: "{count} message",
      messages: "{count} messages",
      activeBranch: "Current version",
    },
    board: {
      faceLabel: "Session face",
      chatFace: "Chat",
      splitFace: "Split",
      dashboardFace: "Dashboard",
      dockMenu: "Chat dock: {dock}",
      dockLeft: "Dock chat left",
      dockRight: "Dock chat right",
      dockBottom: "Dock chat bottom",
      resizeDock: "Resize chat dock",
      enterFullscreen: "Enter fullscreen",
      exitFullscreen: "Exit fullscreen",
      fullscreenUnavailable: "Fullscreen is unavailable in this browser",
      fullscreenFailed: "Could not change fullscreen mode: {error}",
      workboardCard: "Workboard card: {title}, {status}",
      defaultTab: "Main",
      mockPlaceholder: "Board view seam · {tabs} tabs · {widgets} widgets",
      resetTitle: "Reset conversation?",
      resetDescription: "The conversation context resets. Your dashboard stays.",
    },
    catalog: {
      remoteViewOnly: "This session is on a paired device and is view-only.",
      unsupportedViewOnly: "This external session source is view-only.",
      sessionMenu: "External session actions",
      openInOpenClaw: "Open in OpenClaw",
      importToOpenClaw: "Import to OpenClaw",
      importComplete: "Imported {count} transcript items.",
      importUnchanged: "The imported transcript is up to date.",
      importIncomplete:
        "Imported {count} transcript items. Older history exceeded the import limit.",
      openImportedSession: "Open imported session",
      openInTerminal: "Open in terminal",
      deleteSession: "Delete",
      deleteSessionConfirm:
        "Delete this external session from OpenClaw? Make sure no other runner is using it. Beamed sessions are deleted permanently. Sessions kept by another tool, such as Codex, are archived there and may be restorable.",
      terminalUnavailable: "Terminal opening is unavailable for this session.",
    },
    taskSuggestions: {
      eyebrow: "Suggested task · in {repo}",
      startSession: "Start in a new session",
      startOptions: "Choose where to start the task",
      startWorktree: "Start in a new worktree",
      chooseRepository: "Choose repository",
      repositoryFolder: "Repository folder",
      chooseRepositoryHelp:
        "Choose a Git repository with a commit. Your task will start in a new worktree; its instructions are preserved.",
      startCurrentSession: "Start in this session",
      copyPrompt: "Copy prompt",
      copyPromptFailed: "Couldn't copy the prompt to the clipboard",
      promptCopied: "Copied",
      showInstructions: "Show instructions",
      hideInstructions: "Hide instructions",
      previous: "Previous suggested task",
      next: "Next suggested task",
      starting: "Starting…",
      dismiss: "Dismiss {title}",
      started: "Task started",
      startUnconfirmed: "Couldn't confirm the task started. Retry checks the same task.",
      adminRequired: "Administrator access is required to start suggested tasks.",
    },
    pullRequests: {
      open: "Open",
      draft: "Draft",
      merged: "Merged",
      closed: "Closed",
    },
    usageRemaining: "Usage Remaining",
    view: {
      menu: "View",
      speechBubbles: "Speech bubbles",
      activityDetails: "View activity details",
      workingDetails: "Agent is working. View details",
      reasoning: "Reasoning",
      toolCalls: "Tool calls",
      commentary: "Keep commentary",
    },
    sendShortcut: "Send shortcut",
    sendShortcutEnter: "Enter",
    sendShortcutModifierEnter: "⌘/Ctrl+Enter",
    followUpMode: "Follow-ups while the agent is working",
    followUpModeQueue: "Queue until the run ends",
    followUpModeServer: "Server default ({mode})",
    followUpModeLoading: "loading…",
    followUpModeSteer: "Steer into the active run",
    followUpModeOverriding: "Overriding server default ({mode})",
    followUpModeReset: "Reset to server default",
    catalogOpenTarget: "Open external sessions in",
    catalogOpenTargetViewer: "OpenClaw viewer",
    catalogOpenTargetTerminal: "Terminal",
    catalogOutputTruncated: "[Output truncated]",
    onboardingDisabled: "Disabled during setup",
    commandPaletteTitle: "Search or jump to… (⌘K)",
    openCommandPalette: "Open command palette",
    updating: "Updating…",
    actions: {
      copyAsMarkdown: "Copy as markdown",
      dismissError: "Dismiss error",
      exitFocusMode: "Exit focus mode",
      scrollToLatest: "Scroll to latest",
    },
    markdown: {
      truncated: "… truncated ({total} chars, showing first {shown}).",
    },
    mermaid: {
      title: "Mermaid diagram",
      options: "Diagram options",
      diagram: "Show diagram",
      source: "Show source",
      copySource: "Copy source",
      expand: "Expand diagram",
      rendering: "Rendering diagram…",
      error: "This diagram could not be rendered. Check the source or simplify the diagram.",
      rendererError:
        "The Mermaid renderer could not load or finish. Reload the dashboard; if this continues, check proxy or authentication rules for its renderer asset URLs.",
      imageError: "The diagram image could not be displayed. Reload the dashboard to try again.",
    },
    codeBlock: {},
    workspaceConflict: {
      titleOne: "1 cloud workspace conflict",
      titleMany: "{count} cloud workspace conflicts",
      description:
        "OpenClaw kept your local versions and applied the other cloud changes. Inspect the staged result or take its version for a conflicted path.",
      summary: "Local versions kept; inspect or take the cloud version.",
      showCommands: "Show commands",
      morePaths: "+{count} more paths",
      stagedResult: "Staged cloud result",
      copyStagedResult: "Copy staged result ref",
      inspectCloud: "Inspect the first cloud version",
      takeCloud: "Take the first cloud version",
      copyInspectCommand: "Copy cloud inspect command",
      copyTakeCommand: "Copy take-cloud command",
      commandHelp:
        "Run these in Bash or zsh (Git Bash on Windows). If inspect says the path does not exist, the cloud deleted it; verify and remove the local path manually. If checkout reports a file/directory conflict, move or remove the blocking local path, then retry. If the staged ref is missing, the notice is stale; do not change the local path.",
      commandsUnavailable:
        "This filename contains terminal control characters, so OpenClaw will not build a copyable shell command for it. Inspect the staged ref directly and enter the path manually with care.",
      dismiss: "Dismiss workspace conflict notice",
      eventSender: "Cloud workspace",
      eventTitleOne: "Cloud result applied with 1 conflict",
      eventTitleMany: "Cloud result applied with {count} conflicts",
      eventDescription:
        "Local versions were kept for these paths; other cloud changes were applied.",
    },
    compaction: {
      label: "Compacted history",
      savedTokens: "saved {count} tokens",
    },
    sessionReset: {
      label: "Session reset",
      description: "The earlier conversation was cleared.",
    },
    outboxRecoveryReviewTitle: "Review in this chat?",
    outboxRecoveryConfirm: "Add this saved copy to “{chat}” for review? Nothing will be sent.",
    outboxRecoveryRestore: "Restore",
    outboxRecoveryConflict:
      "Keep or clear the draft and pending messages in this chat first, or open another non-Incognito chat. Your saved copy is unchanged.",
    outboxRecoveryStorageFailed:
      "We could not access your saved messages. Reload to try again. Do not clear browser site data if you want to keep them.",
    outboxRecoveryFull:
      "There are more saved messages than we can show. Review or delete the copies below to make room. The remaining messages are still saved in this browser.",
    outboxRecoveryDraft: "Draft",
    outboxRecoveryQueued: "Unsent",
    outboxRecoverySource: "from {chat}",
    outboxRecoveryUpdated: "Last updated {time}",
    outboxRecoveryGoal: "Includes an unsent goal change",
    outboxRecoveryReply: "Reply to: {text}",
    outboxRecoveryAttachments: "Attachments: {files}",
    outboxRecoveryUnconfirmedLabel: "may have been sent",
    outboxRecoveryUnconfirmed:
      "Delivery unconfirmed. This message may already have been sent. Check the original chat before sending it again.",
    outboxRecoveryAttachmentMissing:
      "An attachment could not be loaded. Review the message and reattach the file before sending.",
    outboxRecoveryDelete: "Delete",
    outboxRecoveryDeleteTitle: "Delete this saved copy?",
    outboxRecoveryDeleteConfirm:
      "This removes the draft and any saved messages or attachments shown with it from this browser. It cannot be undone. Messages already sent to a chat will not be deleted.",
    outboxRecoveryDeleteConflict:
      "This saved copy changed while you were reviewing it. Nothing was deleted. Reload and review it again.",
    privateDraftReload: {
      blocked: "An unsent Incognito draft is keeping this tab open. Review it before refreshing.",
      review: "Review private draft",
      title: "Unsent Incognito draft",
      description:
        "This draft stays only in this tab. Copy its text or download its attachments before discarding it to refresh. Keeping this tab open leaves the draft unchanged.",
      text: "Draft text",
      copy: "Copy text",
      goal: "This draft includes an unsent goal change.",
      reading:
        "Some attachments are still being read. Keep this tab open, then review the draft again when they finish.",
      changed:
        "The draft changed while you were reviewing it. It has not been discarded; review the current draft before refreshing.",
      copyFailed:
        "The text could not be copied. Select it in the draft field and copy it manually.",
      attachmentUnavailable:
        "This attachment is no longer available to download. Keep the draft open and recover the original file before discarding it.",
      download: "Download {name}",
      discard: "Discard this draft and refresh",
      keep: "Keep in this tab",
      unavailable:
        "The private draft could not be opened. It remains in this tab; try Review private draft again.",
    },
    incognitoExpiredTitle: "Incognito session expired",
    incognitoExpiredBody:
      "Incognito sessions last for 24 hours or until the Gateway restarts. Your unsent input stays in this tab.",
    newIncognitoSession: "New Incognito session",
    restartRecoveryTitle: "This session ended during a restart.",
    restartRecoveryDisabled: "Its transcript is safe.",
    resumeInNewSession: "Resume in new session",
    resumingSession: "Resuming…",
    systemNotice: {
      guardian: {
        approvedSummary: "Guardian approved {action}.",
        deniedLabel: "Guardian denied",
        deniedSummary: "{action} · risk: {risk}\n\n{rationale}",
        warningLabel: "Guardian warning",
        warningFallback: "Guardian stopped the requested action.",
        strictReviewRequiredLabel: "Guardian review required",
        strictReviewRequiredSummary: "Guardian is reviewing this action before it can continue.",
        requestedAction: "Requested action",
        unknownRisk: "unknown",
        noRationale: "No rationale was provided.",
      },
      restartRecovery: {
        label: "System · restart recovery",
        summary:
          "Turn interrupted by a gateway restart — asked the agent to resume and finish the response.",
        interrupted:
          "The Gateway restarted. Automatic recovery was interrupted before the agent could resume. Send a message to continue.",
        cancelled:
          "The Gateway restarted. Automatic recovery was cancelled before the agent could resume. Send a message to continue.",
      },
      automationRecovery: {
        label: "System · automation recovery",
        summary: "An automation failed — asked the agent to investigate and continue if needed.",
      },
      gatewayRestarted: {
        label: "System · gateway restarted",
      },
      cliHarnessContext: {
        label: "System · injected context",
      },
      claudeCliTaskNotification: {
        label: "System · background task",
      },
      showContent: "Show content",
    },
    skillLearned: {
      label: "Learned",
      created: "Created",
      updated: "Updated",
      archived: "Archived",
      restored: "Restored",
      open: "Open {name} in Skill Workshop",
      undo: "Undo",
      undoing: "Undoing…",
      undone: "Undone",
      undoError: "Could not undo: {error}",
    },
    progressLabels: {
      shelling: "Shelling",
      scuttling: "Scuttling",
      clawing: "Clawing",
      pinching: "Pinching",
      molting: "Molting",
      bubbling: "Bubbling",
      tiding: "Tiding",
      reefing: "Reefing",
      cracking: "Cracking",
      sifting: "Sifting",
      brining: "Brining",
      nautiling: "Nautiling",
      krilling: "Krilling",
      barnacling: "Barnacling",
      lobstering: "Lobstering",
      tidepooling: "Tidepooling",
      pearling: "Pearling",
      snapping: "Snapping",
      surfacing: "Surfacing",
    },
    turnRecap: {
      doneIn: "Done in {duration}",
      tokens: "{count} output tokens",
      tokensOne: "1 output token",
    },
    commands: {},
    skills: {
      menu: "Skill references",
      label: "Skills",
      loading: "Loading skills…",
    },
    splitView: {
      chooseConversation: "Choose a conversation",
      missingOwner:
        "Select this pane, then choose Home or a conversation in the sidebar to restore it.",
      open: "Open split view",
      splitRight: "Split right",
      splitDown: "Split down",
      closePane: "Close pane",
      panePosition: "Column {column}, row {row} ({pane})",
      dropSplit: "Split",
      dropOpenHere: "Open here",
    },
    sidebar: {
      mySessions: "My sessions",
      updateMacAndGateway: "Update Mac app + Gateway",
      updateGateway: "Update Gateway",
      serverUpdatedTitle: "Server updated",
      serverUpdatedRefresh: "Refresh for full capabilities",
      threads: "Sessions",
      otherSessions: "Other",
      groups: "Groups",
      coding: "Coding",
      catalogViewOptions: "View options",
      reorderItem: "Reorder {item}",
      moveUp: "Move up",
      moveDown: "Move down",
      hideFromSidebar: "Hide from sidebar",
      sectionHidden: "{section} hidden.",
      sectionHiddenRecovery: "Show it again in Settings > Appearance > Sidebar.",
      hiddenSessionSections: "Hidden session sections",
      showSessionSection: "Show",
      catalogGroupByProject: "Project",
      catalogGroupByPerson: "Person",
      openSessionMenu: "Open session menu",
      sortBy: "Sort by",
      sortCreated: "Created",
      menuFilters: "Filters",
      menuDisplay: "Display",
      hideEmpty: "Hide empty",
      activeFilterCount: "Active filters: {count}",
      sortSessions: "Filter & sort",
      sessionSources: "Session sources",
      showAllSessions: "Show all sessions",
      noActiveSessionsForFilter: "No active sessions match this filter",
      sortUpdated: "Last updated",
      sessionMenu: "Actions for {session}",
      sessionMenuMany: "Actions for {count} sessions",
      catalogDiscoveryHelp:
        "{error}. Configure automatic session discovery in Settings > Appearance > Session sources.",
      catalogPaginationFailed: "Session catalog returned a repeated page cursor",
      catalogPageMissingHost: "Session catalog page did not include the requested host",
    },
    welcome: { suggestions: { whatCanYouDo: "What can you do?" } },
    runControls: {
      newSession: "New session",
      newSessionWorktree: "New session in worktree",
      exportChat: "Export chat",
      queue: "Queue",
      queueMessage: "Queue message",
      stop: "Stop",
      stopWithShortcut: "Stop (Esc)",
      stopGenerating: "Stop generating",
      send: "Send",
      sendMessage: "Send message",
    },
    queue: {
      reviewEdit: "Review edit",
      reloadBlocked: "Save or cancel your queued message edit before reloading.",
      connectionPending: "Finishing connection recovery. Try sending again when it is ready.",
      editSourceChanged:
        "This queued message changed while you were editing. Your edit is still here. Copy it, cancel the edit, and review the queue before trying again.",
      editStorageFailed:
        "Your edit could not be saved in this browser. Keep this tab open and copy your edit before freeing browser storage, then try again.",
      full: "The message queue is full. Wait for a queued message to send or remove one, then try again.",
      initialTurnPending:
        "The initial message is unresolved. Reconnect if needed, then review it before sending another message.",
      notSent: "Not sent",
      deliveryUnconfirmed: "Delivery unconfirmed",
      checkDelivery: "Check delivery",
      checkDeliveryHelp:
        "Delivery is unconfirmed. Check delivery looks for the original message without resending it or starting a worker. Inspect the conversation, or copy the retained prompt if you choose to start a separate attempt.",
      retry: "Retry",
      retryQueuedMessage: "Retry queued message",
      discard: "Discard",
      discardPendingMessage:
        "Discard this local pending copy. This does not cancel a message already received by the Gateway.",
      steer: "Steer",
      steerQueuedMessage: "Steer queued message",
      removeQueuedMessage: "Remove queued message",
      reorderQueuedMessage: "Reorder queued message with the arrow keys",
      reorderUnavailable: "This message holds its place and cannot be reordered",
      editQueuedMessage: "Edit queued message",
      moreActions: "More queued message actions",
      cancelEdit: "Cancel editing and keep the queued message",
      states: {
        applyingSettings: "Applying chat settings",
        blockedByUnconfirmed:
          "Queue paused. Retry or discard the earlier unconfirmed message in the conversation.",
        runningCommand: "Running command",
        waitingForReconnect: "Waiting for reconnect",
        editing: "Editing",
        needsReview: "Delivery uncertain",
      },
      imageCount: "Image ({count})",
    },
    goals: {},
    asyncQuestions: {
      title: "Optional · work can continue",
      dismiss: "Dismiss",
      dismissing: "Dismissing…",
      dismissed: "Dismissed",
      reopening: "Restoring answer…",
      dismissedReason: "You can still answer this question later.",
      dismissedNotice: "Question dismissed. Work continues.",
      pendingOne: "{count} unanswered question",
      pendingMany: "{count} unanswered questions",
      inComposer: "Answer above the message box.",
      archived: "No longer pending",
      archivedReason: "The conversation moved on. You can still answer this question.",
      sendFailed: "Could not send your answer. Your draft is preserved.",
      queued: "Answer queued",
      sending: "Sending answer…",
      sent: "Answer sent",
      failed: "Answer not sent",
      awaitingConfirmation: "Awaiting delivery confirmation",
      retry: "Retry answer",
      draftStorageFailed:
        "This answer is not saved on this device. Keep this page open or copy your answer.",
      draftConflict: "This question was edited in another tab. Copy your answer before reloading.",
    },
    questions: {
      showSecret: "Show secret",
      hideSecret: "Hide secret",
      resourcePreview: "Preview resource",
      addResources: "Add resources",
      resourceUploadTooLarge: "Selected resources exceed the upload limits.",
      other: "Type your own answer here",
      multilineHint: "Enter adds a line · {shortcut} to continue",
      answer: "Answer",
      answerPlaceholder: "{label}",
      openLink: "Open link",
      externalStepHint: "Complete the step in the new tab, then return here to submit your answer.",
      submit: "Submit",
      next: "Next",
      back: "Back",
      skip: "Skip",
      skipping: "Skipping…",
      skipped: "Skipped",
      collapse: "Collapse question",
      expand: "Expand question",
      eyebrow: "Waiting for your answer",
      summaryLabel: "Question outcome",
      ownAnswerFor: "Your own answer for {header}",
      storeRequestedBy: "Requested by {agent} · {session}",
      storeEntry: "Stores {name} as {kind}",
      storeReplacement: "Replaces {name} — last updated {updated}",
      storeReplacementBy: "Replaces {name} — last updated {updated} by {updatedBy}",
      submitting: "Submitting…",
      submitFailed: "Could not submit: {error}",
      answered: "Answered",
      answeredElsewhere: "Answered elsewhere",
      unavailable: "Unavailable",
      expired: "Expired",
      disconnected: "Not connected. Try again after reconnecting.",
      loadFailed: "Could not load this question. Try again.",
    },
    imageLightbox: {
      actions: "Image actions",
      previous: "Previous image",
      next: "Next image",
      position: "{current} / {total}",
      label: "Image preview: {title}",
      open: "Open image {title}",
      openOriginal: "Open in new tab",
      copy: "Copy image",
      download: "Download image",
      copyFailed: "Could not copy this image. Check clipboard access and try again.",
      downloadFailed: "Could not download this image. Try again.",
      loadFailed: "Could not load this image. Try again.",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      resetZoom: "Reset zoom",
      close: "Close image preview",
      untitled: "Image",
    },
    externalImage: { notLoaded: "External image not loaded", open: "Open image" },
    messages: {
      copySelection: "Copy",
      replyingTo: "Replying to {name}",
      message: "message",
      showLess: "Show less",
      showMore: "Show more",
      tooLargeToDisplay: "This message is too large to display here.",
      unattributedSender: "Message",
      toolSender: "Tool",
      errorSender: "Error",
    },
    mediaPlayer: {
      play: "Play",
      pause: "Pause",
      seek: "Seek media",
      mute: "Mute",
      unmute: "Unmute",
      volume: "Volume",
      download: "Download {filename}",
      preparing: "Preparing playback…",
      openVideo: "Expand {filename} in the media overlay",
      videoPreview: "Video preview: {title}",
      closeVideoPreview: "Close video preview",
    },
    youtube: {
      provider: "YouTube",
      video: "YouTube video",
      player: "YouTube player: {title}",
      play: "Play {title}",
      open: "Open on YouTube",
      openVideo: "Open {title} on YouTube",
      close: "Close player",
      strict: "Inline playback is disabled in strict embed mode. Open this video on YouTube.",
      narrow: "Open this video on YouTube to play it in a larger window.",
    },
    modelControls: {},
    nativeRuntimeRecovery: {},
    permissionControls: {},
    rail: {
      title: "Side chat",
      subtitle: "Ask about this session or its project",
      expand: "Expand side chat",
      collapse: "Collapse side chat",
      close: "Close side chat",
      show: "Show side chat",
      clear: "Clear side chat",
      pullRequests: "Pull requests",
      checksPassing: "{count} passed",
      checksFailing: "{count} failed",
      checksPending: "{count} running",
      finished: "Finished {time}",
      empty: "Answers come from this session's transcript and its project files.",
      starters: {
        changed: "What changed?",
        stopped: "Why did it stop?",
        remaining: "What's left?",
      },
      askLabel: "Ask in side chat",
      askPlaceholder: "Ask a question",
      askImageQuestion: "What does this image show?",
      selectionTooLong:
        "These comments are too long for Side chat. Use a shorter selection or comment.",
      selectionQuoteOnly:
        "The selection is too long to attach. A short quote is ready in Side chat.",
      askSubmit: "Ask",
      askPending: "Answering from this session…",
      askBusy: "Side chat is already answering a question.",
      askHistoryUnavailable: "Couldn't load this session's history.",
      askMissing: "This session is no longer available.",
      askModelUnavailable: "No utility model is configured for this session.",
      askImageUnsupported:
        "This Side chat model cannot read images. Choose an image-capable utility model, then retry.",
      askRateLimited: "Side chat reached its question limit. Try again shortly.",
      askUnavailable: "Side chat cannot answer right now.",
      askRetry: "Retry",
      asOf: "as of {time}",
      health: {
        "on-track": "On track",
        grinding: "Working",
        stuck: "Stuck",
        "waiting-on-user": "Waiting on you",
        "wrapping-up": "Wrapping up",
        done: "Done",
        failed: "Failed",
      },
    },
    detailPanel: {},
    sidebarColumns: {
      close: "Close {panel}",
    },
    sidePanel: {
      label: "Side panel",
      addTab: "Add side panel tab",
      expand: "Focus",
      expandPanel: "Expand {panel}",
      restore: "Restore split",
      useViewAsDefault: "Use current view as default",
      currentViewIsDefault: "This is the default view",
      defaultViewDescription:
        "Used when anyone opens this dashboard. Personal layout choices still apply.",
      savingDefault: "Saving default…",
      defaultSaved: "Dashboard default saved for future opens.",
      defaultSaveFailed:
        "Could not save the dashboard default. Check the connection and try again.",
      defaultSaveError: "Could not save the dashboard default: {error}",
      swap: "Swap {main} and {side}",
      layout: "Layout",
      dockLeft: "Move side panel left",
      dockRight: "Move side panel right",
      dockBottom: "Move side panel below",
      conversation: "Chat",
      conversationEmpty: "The conversation for this task.",
      minimize: "Minimize side panel",
      resize: "Resize side panel",
      review: "Review",
      reviewEmpty: "Open a change, file, image, or tool result to review it here.",
      terminal: "Terminal",
      terminalEmpty: "Open a shell for this session's workspace.",
      browser: "Browser",
      browserEmpty: "A shared browser for you and the agent.",
      files: "Files",
      filesEmpty: "Browse files, artifacts, and changes from this session.",
      companion: "Side chat",
      companionEmpty: "Ask a focused question about this session.",
      processes: "Processes",
      processesEmpty: "Inspect background commands and their output for this conversation.",
      subagents: "Subagents",
      subagentsEmpty: "Follow delegated work from this conversation.",
      desktop: "Desktop",
      desktopEmpty: "Connect to an available remote desktop.",
      portal: "Portal",
      portalEmpty: "Preview a web application running for this task.",
      discussion: "Discussion",
      discussionEmpty: "Open the shared discussion for this session.",
      dashboard: "Dashboard",
      dashboardEmpty: "This task does not have a dashboard yet.",
    },
    thread: {
      search: "Search messages",
      searchPlaceholder: "Search messages...",
      closeSearch: "Close search",
      loading: "Loading chat",
      showEarlier: "Show earlier",
      loadingEarlier: "Loading earlier…",
      noMatches: "No matching messages",
      positionRail: "Conversation position",
      positionMarker: "{label}, marker {position} of {count}",
      positionUserMessage: "User message",
      positionAssistantMessage: "Assistant message",
      positionMarkerHint:
        "Use arrow keys or Home and End to choose a marker, Enter or Space to jump, and Escape to return to the conversation. Tab leaves the rail.",
    },
    pendingInputs: {
      agentUpdate: {
        queued: "Agent update received · waiting for delivery.",
        cancelled: "This agent update was cancelled before delivery.",
        interrupted: "Delivery interrupted. This saved agent update is not scheduled to retry.",
      },
      waitingForWorkspaceSync: "Received · waiting for workspace sync",
      waitingForWorkerSetup: "Received · waiting for worker setup",
      resuming:
        "Interrupted by a Gateway restart. This saved message will resume when the session is ready.",
      cancelled:
        "Cancelled before the agent started it. It will not run automatically; copy it and send again.",
      interrupted:
        "Interrupted before the agent started it. It will not run automatically; copy it and send again.",
      earlier: "Show earlier messages",
      latest: "Show latest messages",
    },
    pairingQrExpired: {
      title: "Pairing QR expired",
      reason: "Run /pair qr again to generate a fresh setup code.",
      badge: "Expired",
    },
    composer: {
      running: "running",
      composerInput: "Chat composer",
      emojiSuggestions: "Emoji suggestions",
      placeholder: "Message {name}",
      emptyHint: "Write a message to send.",
      placeholderWithAttachments: "Add a message or paste more images...",
      preparingModel: "Preparing model...",
      responding: "{name} is responding...",
      sendingMessage: "Sending message...",
      working: "{name} is working...",
      runDone: "Done",
      runInterrupted: "Interrupted",
      runStatus: "Run status: {status}",
      compactingContext: "Compacting context...",
      contextCompacted: "Context compacted",
      fallbackActive: "Fallback active: {model}",
      modelFallbackEvent: "Model fallback: {from} → {to}",
      modelRestoredEvent: "Returned to selected model: {from} → {to}",
      fallbackCleared: "Fallback cleared: {model}",
      fallbackSelected: "Selected: {model}",
      fallbackCurrent: "Active: {model}",
      fallbackPrevious: "Previous fallback: {model}",
      fallbackReason: "Reason: {reason}",
      fallbackAttempts: "Attempts: {attempts}",
      cancelReply: "Cancel reply",
      attachmentPreview: "Attachment preview",
      browserAnnotation: "Browser annotation",
      browserAnnotationPreview: "Browser annotation preview",
      browserAnnotationRegion: "{count} marked region",
      browserAnnotationRegions: "{count} marked regions",
      browserAnnotationInspectedElement: "Element inspected",
      browserAnnotationRemoved: "Browser annotation removed.",
      browserAnnotationUndoUnavailable:
        "Undo is unavailable because the browser annotation limit has been reached.",
      preparingAttachments: "Preparing attachments…",
      preparingAttachmentCount: "Preparing {count} attachment",
      preparingAttachmentsCount: "Preparing {count} attachments",
      removeAttachment: "Remove attachment",
      removeNamedAttachment: "Remove {name}",
      removeBrowserAnnotation: "Remove browser annotation: {name}",
      addAttachment: "Add attachment",
      attach: "Attach…",
      attachPhoto: "Photo",
      attachFileOption: "File",
      menu: {
        back: "Back",
        skills: "Skills",
        connectors: "Connectors",
        webSearch: "Web search",
        webSearchGloballyDisabled:
          "Web search is disabled in config (tools.web.search.enabled). Session overrides cannot turn it back on.",
        webSearchClearStaleEnable:
          "Web search is disabled in config. Click to clear this session’s enable override.",
        managePlugins: "Manage plugins",
        manageSkills: "Manage skills",
        addMcpServer: "Add MCP server…",
        addMcpServerTitle: "Add MCP server",
        addMcpServerDescription: "Configure the server and choose where it is enabled.",
        scopeLabel: "Availability",
        scopeSession: "This session",
        scopeEverywhere: "Everywhere",
        scopeSessionHint:
          "The server is saved globally disabled and enabled only for this session.",
        scopeEverywhereHint: "The server is saved and enabled for every session.",
        toolAccess: {
          label: "Tool access",
          loading: "Loading tools…",
          loadFailed: "Couldn’t load tools.",
          noTools: "No tools available for this connector.",
          summary: "{enabled} of {total} tools on",
          summaryOne: "{enabled} of {total} tool on",
        },
        enabledCount: "{count} on",
        loadingSkills: "Loading skills…",
        skillsLoadFailed: "Couldn’t load skills.",
        noSkills: "No skills available.",
        noConnectors: "No MCP servers configured.",
        depsMissing: "deps missing",
        skillBlocked: "not available for this agent",
        sessionTag: "session",
        offlineBlocked: "Connect to the gateway to change session capabilities.",
        adminBlocked: "Admin access is required to manage connectors.",
        savingBlocked: "Wait for the current session capability change to finish.",
      },
      overrides: {
        count: "{count} overrides",
        countOne: "{count} override",
        clear: "Clear session overrides",
      },
      contextUsage: {
        title: "Context usage details",
        open: "Open context usage details",
        summary: "Session context usage: {used} of {limit} ({pct}%)",
        contextWindow: "Context window",
        promptBudget: "Prompt budget",
        latestRunTokens: "Latest run tokens",
        estimatedCost: "Est. cost",
        planUsage: "Plan usage",
        openUsage: "Open usage dashboard",
        resets: "Resets {time}",
        usageCredits: "Usage credits",
        budgetValue: "{used} of {limit}",
        limitFiveHour: "5-hour limit",
        limitWeekly: "Weekly",
        limitDaily: "Daily limit",
        limitHours: "{hours}-hour limit",
      },
      takePhoto: "Take photo",
      cameraAccessFailed: "Unable to access the camera.",
      cameraBusy: "The camera is busy or unavailable to the browser.",
      cameraFallback: "Camera {number}",
      cameraInput: "Camera",
      cameraListUnsupported: "This browser cannot list cameras.",
      cameraNoneFound: "No camera was found.",
      cameraPageInactive: "Cameras are unavailable while this page is inactive.",
      cameraPermissionBlocked:
        "Camera access is blocked. Allow camera access in browser site settings.",
      cameraPreview: "Camera preview",
      dismissVoiceInputError: "Dismiss voice input error",
      microphoneAccessFailed: "Unable to access microphone inputs.",
      microphoneAccessPending:
        "Waiting for microphone access. Bring this tab to the foreground and allow access if prompted.",
      microphoneBusy: "Microphone inputs are busy or unavailable to the browser.",
      microphoneStopped: "Microphone input stopped. Choose an available input and start again.",
      microphoneFallback: "Microphone {number}",
      microphoneInput: "Microphone input",
      microphoneAppliesNextSession: "Changes apply when you start your next Talk session.",
      microphoneListUnsupported: "This browser cannot list microphone inputs.",
      noCameras: "No additional cameras found",
      noMicrophones: "No additional microphones found",
      microphoneNoneFound: "No microphone found. Plug one in and it appears here.",
      microphonePageInactive: "Microphone inputs are unavailable while this page is inactive.",
      microphonePermissionBlocked:
        "Microphone access is blocked. Allow it in browser site settings to list inputs.",
      realtimeTalkCapability: "Tap to talk",
      dictationCapability: "Dictation",
      realtimeTalkProviderUnavailable: "No realtime voice provider is configured.",
      dictationProviderUnavailableShort: "No transcription provider is configured.",
      talkCapabilityChecking: "Checking provider availability…",
      talkCapabilityUnknown: "Provider availability could not be verified.",
      openRealtimeTalkSettings: "Talk settings",
      openDictationSettings: "Transcription setup",
      configureCapability: "Configure",
      holdToRecordSetting: "Hold microphone button to start dictation",
      holdToDictate: "Hold to start dictation",
      holdToRecordSettingDescription:
        "Hold until dictation starts, then release and keep speaking. Tap Stop to insert the transcript.",
      dictationAudioUnsupported: "The Gateway returned an unsupported dictation audio format.",
      dictationBrowserAudioUnsupported: "This browser cannot capture dictation audio at 8 kHz.",
      dictationDisconnected: "Dictation stopped because the Gateway disconnected.",
      dictationFailed: "Dictation failed.",
      dictationInterruptedRecovery:
        "Captured text was inserted. Check your connection and try again.",
      dictationStartRecovery: "Check microphone access and transcription settings, then try again.",
      dictationFinalizing: "Finishing dictation…",
      dictationListening: "Listening…",
      dictationStopAndKeep: "Stop and keep text",
      dictationProviderUnavailable:
        "No transcription provider is configured for dictation. Choose one in Settings to dictate.",
      realtimeTalkCancellationRejected: "Realtime output cancellation was not accepted.",
      realtimeTalkMissingTurnIdentity: "Realtime output is missing its turn identity.",
      realtimeTalkRequiresMicrophone: "Realtime voice input requires browser microphone access.",
      selectedMicrophoneUnavailable:
        "The selected microphone is unavailable. Choose another input or System default.",
      selectedCameraUnavailable:
        "The selected camera is unavailable. Choose another camera or System default.",
      startVoiceInput: "Start voice input",
      voiceGestureHint: "Tap to talk · Hold to start dictation",
      stillListening: "Still listening",
      stopVoiceInput: "Stop voice input",
      systemDefaultMicrophone: "System default",
      useSystemDefaultMicrophoneForCall: "Use System default for this call",
      systemDefaultCamera: "System default",
      switchCamera: "Switch camera",
      turnCameraOff: "Turn camera off",
      turnCameraOn: "Turn camera on",
      voiceTranscript: "Voice transcript",
    },
    attachments: {
      allowImage: "Allow image",
      attachedFile: "Attached file",
      audio: "Audio",
      download: "Download",
      expand: "Open {filename} in the side panel",
      open: "Open",
      previewUnavailable: "Preview unavailable",
      textPreviewUnavailable:
        "Could not preview this file. Text previews require UTF-8 files up to 256 KiB. Download it to read the full file.",
      htmlPreviewUnavailable:
        "Could not preview this HTML file. HTML previews require UTF-8 files up to 2 MiB. Download it to read the full file.",
      readFailed: "Could not attach: {names}{more}",
      imagesOnly: "Side chat accepts image attachments only.",
      tooLarge: "Too large to send: {names}{more}",
      showInTextField: "Show in text field",
      pastedText: "Pasted text",
      outsideAllowedFolders: "Outside allowed folders",
      unavailable: "Unavailable",
      failureDeliveryFailed: "Delivery failed. Try sending this file again.",
      failureInvalidReference:
        "Use a public HTTPS URL without credentials or attach a local file by a safe path.",
      failureFileNotFound: "File not found. Check the path and try again.",
      failureUnsupportedFormat:
        "Rejected by the local attachment allowlist. Send a supported file type.",
      history: "History",
      image: "Image",
      notSent: "Not sent",
      omittedFromHistory: "Omitted from history",
      omittedFromHistoryWithSize: "Omitted from history · {size}",
      video: "Video",
    },
    voice: {
      asking: "Asking OpenClaw...",
      replyFailed: "The voice reply was interrupted. Start Talk again to retry.",
      preparing: "Preparing voice session...",
      connecting: "Connecting voice input...",
      listening: "Listening...",
      selectionFailed: "Could not change voices. Restart the call.",
      selectionTimedOut: "Voice change timed out. Restart the call.",
      selectionConfirmationFailed: "Voice change unconfirmed.",
    },
    selectors: {
      loadMoreSessions: "Show more",
      loadMoreRosterSessions: "Load more sessions",
      model: "Chat model",
      modelSection: "Model",
      modelLocked: "Locked",
      modelLockedLabel: "Model selection controlled for this session",
      lockedSessionModel: "Session model",
      thinkingLevel: "Chat thinking level",
    },
    permissions: {
      title: "Grant {permissions}",
      neededOn: "OpenClaw needs access on {node} to continue this action.",
      openApp:
        "Grant access in the OpenClaw Mac app on that device: Settings → This Mac → Permissions. Then tell the agent to try again.",
      ready: "Access is available. You can ask the agent to try again.",
      following: "Then grant the remaining permissions: {permissions}.",
      actions: {
        "not-determined": "Grant {permissions}",
        denied: "Open System Settings",
        "restart-required": "Relaunch OpenClaw",
        "stale-grant": "Open System Settings",
        "disabled-in-openclaw": "Enable {permissions}",
      },
      tryAgain: "Try again",
      retryMessage: "I granted {permissions}, try again.",
      states: {
        "not-determined": "Choose Grant to review the permission request.",
        denied: "Open System Settings to allow access for OpenClaw.",
        "restart-required":
          "If you enabled access in System Settings, relaunch OpenClaw to make it available.",
        "stale-grant":
          "macOS may be using a stale permission grant. Open System Settings to review it.",
        "disabled-in-openclaw": "Choose Enable to review and enable this capability in OpenClaw.",
      },
      capabilities: {
        notifications: "Notifications",
        accessibility: "Accessibility",
        eventPosting: "Event Posting",
        screenRecording: "Screen Recording",
        microphone: "Microphone",
        speechRecognition: "Speech Recognition",
        camera: "Camera",
        cameraEnabled: "Camera in OpenClaw",
        location: "Location",
        computerControl: "Computer Control",
        canvas: "Canvas",
        automation: "Automation",
      },
    },
    toolCards: {
      tool: "Tool",
      canvas: "Canvas",
      summary: "Summary",
      toolInput: "Tool input",
      toolOutput: "Tool output",
      providerResponse: "Provider tool response",
      executionOutput: "Execution output",
      showFullOutput: "Show full output",
      fullOutputUnavailable: "Full output unavailable. Only the captured output is shown.",
      copyOutput: "Copy available output",
      downloadOutput: "Download available output",
      outputLoadFailed: "Could not load the full output. Try again.",
      outputDownloadFailed: "Could not download the output. Try again.",
      toolError: "Tool error",
      rawDetails: "Raw details",
      activity: {
        titleOne: "1 operation",
        titleMany: "{count} operations",
        commandsOne: "1 command",
        commandsMany: "{count} commands",
        readsOne: "1 read",
        readsMany: "{count} reads",
        editsOne: "1 edit",
        editsMany: "{count} edits",
        writesOne: "1 write",
        writesMany: "{count} writes",
        searchesOne: "1 search",
        searchesMany: "{count} searches",
        fetchesOne: "1 fetch",
        fetchesMany: "{count} fetches",
        otherOne: "1 other operation",
        otherMany: "{count} other operations",
        subagentsOne: "1 subagent",
        subagentsMany: "{count} subagents",
        failed: "{count} failed",
        blocked: "{count} blocked",
        unknown: "{count} unknown",
      },
      showRawDetails: "Show raw details",
      hideRawDetails: "Hide raw details",
      viewMode: "Tool detail view",
      diff: "Diff",
      raw: "Raw",
      openFile: "Open file",
      openDetails: "Open tool details in side panel",
      widgetActions: "Widget actions",
      copyToClipboard: "Copy to clipboard",
      downloadFile: "Download file",
      copyAsImage: "Copy as image",
      downloadAsImage: "Download as image",
      widgetExportRerender: "This widget needs to be re-rendered to export as an image.",
      widgetExportHtmlFallback: "Image unavailable. Downloaded the widget as HTML instead.",
      widgetExportFailed: "Widget export failed. Try again.",
      pinToDashboard: "Pin to dashboard",
      pinToDashboardPending: "Pinning…",
      pinToDashboardFailed: "Could not pin to dashboard. Try again.",
      pinnedToDashboard: "Pinned",
      fileChanges: "File changes",
      attemptedChanges: "Attempted changes",
      failed: "failed",
      skipped: "Skipped",
      skippedCount: "{count} skipped",
      running: "Running",
      subagentRunning: "running",
      subagentStopped: "stopped",
      openSubagent: "Open subagent session",
      completed: "Completed",
      blocked: "Blocked",
      outcomeUnknown: "Outcome unknown",
      exitCode: "Exit code {code}",
      failureCount: "{count} failed",
      noOutputFailed: "No output — tool failed.",
      noOutputSucceeded: "No output — tool completed successfully.",
      noResult: "No result available.",
      review: {
        reviewing: "{reviewer} reviewing",
        approved: "{reviewer} approved",
        denied: "{reviewer} denied",
        timedOut: "{reviewer} timed out",
        aborted: "{reviewer} stopped",
        risk: "{level} risk",
        authorization: "{level} authorization",
        noRationale: "No rationale was provided.",
      },
      verbs: {
        read: "Read",
        edit: "Edit",
        editing: "Editing",
        edited: "Edited",
        create: "Create",
        creating: "Creating",
        created: "Created",
        delete: "Delete",
        deleting: "Deleting",
        deleted: "Deleted",
        change: "Change",
        changing: "Changing",
        changed: "Changed",
        write: "Write",
        writing: "Writing",
        wrote: "Wrote",
        searched: "Searched",
        fetched: "Fetched",
      },
    },
    workRun: {
      workedFor: "Worked for {duration}",
      worked: "Worked",
      toolCallsOne: "1 tool call",
      toolCallsMany: "{count} tool calls",
    },
    sessionDiff: {
      title: "Changes",
      show: "Show session changes",
      refresh: "Refresh changes",
      loading: "Loading changes…",
      empty: "No changes in this session's checkout.",
      notGit: "This session's workspace is not a git checkout.",
      unknownSession: "No workspace is associated with this session.",
      unknownCommit: "This commit is no longer available in the session checkout.",
      workspaceStopped:
        "Saved changed files are shown. Start the cloud session to load full diffs and line counts.",
      workspaceStoppedFile: "Start the cloud session to load this diff.",
      disconnected: "Gateway is disconnected.",
      allChanges: "All Changes",
      uncommitted: "Uncommitted",
      commitsAhead: "{count} commits ahead of {base}",
      head: "HEAD",
      mergeBase: "Merge Base",
      scopeMenu: "Choose change scope",
      sync: "Sync",
      syncLocally: "Sync Locally",
      syncDescription:
        "Run this command in a local checkout to mirror this session's committed changes.",
      copyCommand: "Copy sync command",
      checkoutPath: "Checkout path",
      branchName: "Branch name",
      uncommittedStay: "Uncommitted changes stay in the session checkout.",
      viewOptions: "Change view options",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      enableWrapping: "Enable Wrapping",
      disableWrapping: "Disable Wrapping",
      switchSplit: "Switch to Split Diff",
      switchUnified: "Switch to Unified Diff",
      fileActions: "Actions for {path}",
      copyPath: "Copy Path",
      openFile: "Open File",
      openInEditor: "Open in Editor",
      revealInFileTree: "Reveal in File Tree",
      unmodifiedLines: "{count} unmodified lines",
      expandPreviousLines: "Show previous {count} unmodified lines",
      expandNextLines: "Show next {count} unmodified lines",
      expandAllLines: "Show all {count} unmodified lines",
      binaryFile: "Binary file",
      untracked: "untracked",
      previewUnavailable: "Diff preview is unavailable.",
      truncatedFile: "Diff truncated.",
      truncatedResult: "Some changes could not be displayed.",
      statusAdded: "Added",
      statusDeleted: "Deleted",
      statusRenamed: "Renamed",
      statusModified: "Modified",
    },
    sessionDiscussion: {
      show: "Show discussion",
      hide: "Hide discussion",
      disconnected: "Gateway is disconnected.",
      loading: "Loading discussion…",
      opening: "Opening discussion…",
      requiresWriteAccess: "Operator write access is required to open this discussion.",
      openExternal: "Open discussion in a new tab",
      frameTitle: "Session discussion",
      unavailable: "This discussion cannot be embedded.",
    },
    workspaceFiles: {
      label: "Session workspace",
      collapse: "Collapse session workspace",
      showFiles: "Show session files",
      dockRight: "Dock to the right",
      dockBottom: "Dock to the bottom",
      workspace: "Session",
      files: "Workspace",
      refresh: "Refresh session workspace",
      loading: "Loading session workspace…",
      changed: "Changed",
      read: "Read",
      artifacts: "Artifacts",
      browser: "Project files",
      root: "Root",
      search: "Search files",
      searchResults: "Search results",
      parentFolder: "Parent folder",
      noBrowserFiles: "No files in this folder.",
      folderUnavailable: "This folder is unavailable. Open its parent folder to continue browsing.",
      noSearchResults: "No matching files.",
      truncated: "Showing the first matching files. Refine the search to narrow results.",
      session: "Session",
      missing: "Missing",
      filterAll: "All",
      filters: "Filter files",
      changedCount: "{count} changed",
      readCount: "{count} read",
      artifactCount: "{count} artifacts",
      actions: "Workspace file actions",
      preview: "Preview",
      copyPath: "Copy path",
    },
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
    es: "Español (Spanish)",
    jaJP: "日本語 (Japanese)",
    ko: "한국어 (Korean)",
    fr: "Français (French)",
    hi: "हिन्दी (Hindi)",
    ar: "العربية (Arabic)",
    it: "Italiano (Italian)",
    tr: "Türkçe (Turkish)",
    uk: "Українська (Ukrainian)",
    id: "Bahasa Indonesia (Indonesian)",
    pl: "Polski (Polish)",
    th: "ไทย (Thai)",
    vi: "Tiếng Việt (Vietnamese)",
    nl: "Nederlands (Dutch)",
    fa: "فارسی (Persian)",
    ru: "Русский (Russian)",
  },
  secretsStore: {
    name: "Name",
    access: "Access",
    accessMode: "Access mode",
    value: "Value",
    allowedHosts: "Allowed hosts",
    allowedHostsPlaceholder: "api.example.com",
    allowedHostsHint:
      "Exact hostnames only, one per line or comma-separated. No wildcards or ports.",
    noAllowedHosts: "None",
    updated: "Last updated",
    by: "{time} by {name}",
    actions: "Actions",
    add: "Add",
    edit: "Edit",
    bulk: "Bulk Add",
    protectedSecret: "Protected secret",
    protectedSecretHint:
      "Hidden after save and inert unless referenced by a SecretRef or used through enabled destination-bound Gateway egress. It is never directly readable.",
    agentReadable: "Agent-readable environment",
    agentReadableHint:
      "High risk: visible to admins and plaintext to Gateway-hosted agent commands. The agent can print, transmit, or persist it. Applies from the next run.",
    hint: "Choose protected, write-only secrets or intentionally agent-readable Gateway environment values.",
    required: "Enter a value.",
    detect: "Protect credential-like names automatically",
    detected: "{count} protected secrets detected",
    detectedOne: "{count} protected secret detected",
    unavail: "Gateway/admin required.",
    badName: "Use SERVICE_API_KEY.",
    tooLarge: "Max 64 KiB.",
    savedProtected:
      "Saved {name} as Protected secret. Add a SecretRef or enable destination-bound Gateway egress to use it.",
    savedReadable:
      "Saved {name} as Agent-readable environment. It is available to Gateway-hosted agent commands from the next run.",
    savedMany:
      "Saved {count} entries ({protected} protected, {readable} agent-readable). Protected secrets need a SecretRef or enabled destination-bound Gateway egress; agent-readable environment values reach Gateway-hosted agent commands from the next run.",
    warnings: "{count} runtime warnings.",
    partial: "{saved}/{total}: {error}",
    confirmDelete: "Delete {name}?",
    deleted: "Deleted {name}.",
    deleteFailed: "The secret was not deleted. Reload the list and try again.",
  },
  cron: {
    adminRequired: "Browsing only. Automation changes require operator.admin access.",
    tabs: {
      filterLabel: "Automation status",
      all: "All",
      active: "Active",
      paused: "Paused",
    },
    list: {},
    suggestions: {},
    stats: {
      tasks: "Automations",
      failing: "Failing",
      nextWake: "Next wake",
    },
    detail: {
      tabsLabel: "Automation details",
      newTitle: "New automation",
      newSubtitle: "Describe what OpenClaw should do, then pick when it runs.",
      back: "All automations",
      settingsTab: "Settings",
      historyTitle: "Run history",
      generalSection: "General",
      scheduleSection: "Schedule",
      deliverySection: "Delivery",
      active: "Active",
      paused: "Paused",
    },
    actions: {
      runNow: "Run now",
      runNowJob: "Run now: {name}",
      runIfDue: "Run if due",
      pauseJob: "Pause: {name}",
      resumeJob: "Resume: {name}",
      clone: "Clone",
      remove: "Remove",
      removeConfirmTitle: 'Remove "{name}"?',
      removeConfirmMessage:
        "This permanently deletes the automation and stops all future runs. This action cannot be undone.",
      moreJob: "More actions for {name}",
    },
    runNotStarted: {
      notDue: "This automation is not due yet.",
      alreadyRunning: "This automation is already running.",
      recoveryPending: "Scheduler recovery is still in progress.",
      invalidSpec: "This automation has an invalid schedule or payload.",
      stopped: "The scheduler is stopped.",
      unknown: "This automation could not be started.",
    },
    jobs: {
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      condition: "Condition",
      conditional: "Conditional",
      unconditional: "Unconditional",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
    },
    runs: {
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or task",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      noMatching: "No matching runs.",
      emptyTitle: "No runs yet",
      emptyHint: "Runs show up here once an automation fires.",
      conditionActivity: "Condition activity",
      conditionActivityHint: "Checks run on schedule before the payload starts.",
      checks: "Checks",
      lastChecked: "Last checked",
      lastFired: "Last fired",
      notChecked: "Not checked yet",
      neverFired: "Never",
      emptyConditionTitle: "No payload runs yet",
      emptyConditionHint:
        "The condition has been checked {count} times without matching. Run history starts when the payload executes.",
      emptyConditionHintOne:
        "The condition has been checked once without matching. Run history starts when the payload executes.",
      emptyConditionUnchecked:
        "The condition has not been checked yet. Run history starts when the payload executes.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      runStatusRunning: "Running",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      requiredSr: "required",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this task",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      promptPlaceholder: "Describe what OpenClaw should do...",
      repeat: "Repeat",
      repeatInterval: "Interval",
      repeatOnce: "Once",
      repeatOnExit: "On exit",
      repeatStream: "Stream source",
      onExitHelp: "Runs when a watched command exits. The schedule cannot be edited here.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      summaryEverySecondOne: "Runs every second",
      summaryEveryMinuteOne: "Runs every minute",
      summaryEveryHourOne: "Runs every hour",
      summaryEveryDayOne: "Runs every day",
      summaryEverySeconds: "Runs every {amount} seconds",
      summaryEveryMinutes: "Runs every {amount} minutes",
      summaryEveryHours: "Runs every {amount} hours",
      summaryEveryDays: "Runs every {amount} days",
      summaryOnce: "Runs once at {at}",
      summaryCron: "Cron schedule {expr}",
      summaryCronTz: "Cron schedule {expr} ({tz})",
      timezoneOptional: "Timezone",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp:
        "Optional. Leave blank to use the Gateway host timezone, or enter any valid IANA timezone.",
      runsIn: "Runs in",
      mainSession: "Main session",
      isolatedSession: "Isolated session",
      sessionHelp: "Main posts into the agent's timeline. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      action: "Action",
      command: "Command",
      script: "Script",
      readOnlyPayloadHelp:
        "This payload was created outside Control UI. Its contents stay read-only and are preserved when you save other changes.",
      systemEvent: "Post to main timeline",
      agentTurn: "Run agent task",
      systemEventHelp: "Sends your text to the gateway main timeline (good for reminders).",
      agentTurnHelp: "Starts an agent run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp: "Optional. Leave blank to use the gateway default timeout for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Prompt",
      deliveryModeLabel: "Mode",
      selectDeliveryMode: "Choose a delivery mode",
      announceDefault: "Announce summary",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional condition checks, delivery guarantees, schedule jitter, and model controls.",
      conditionTrigger: "Condition trigger",
      conditionTriggerHelp:
        "Run a quiet headless check before the task and call the model only when it matches.",
      triggerConfigured: "Trigger configured",
      triggerScript: "Trigger script",
      triggerScriptHelp:
        "Runs unattended with this automation's tool policy. Return json({ fire, message?, state? }); limits: 30 seconds, 5 tool calls, 16 KB state.",
      triggerOnce: "Disable after first match",
      triggerOnceHelp: "Disable this automation after the first successful fired task.",
      triggerDisabled: "Condition triggers are disabled by cron.triggers.enabled.",
      triggerDisabledConfigured:
        "Condition triggers are disabled. Existing configuration is preserved until you clear it.",
      clearTrigger: "Clear trigger",
      startEnabled: "Start enabled",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this task to use the gateway default agent.",
      sessionKey: "Session key",
      sessionKeyHelp: "Optional routing key for job delivery and wake routing.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      accountId: "Account ID",
      accountIdHelp: "Optional channel account ID for multi-account setups.",
      lightContext: "Light context",
      lightContextHelp: "Use lightweight bootstrap context for this agent job.",
      model: "Model",
      customModel: "Custom model…",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp:
        "Start typing to pick a known model, or enter a custom one. Routine jobs (summaries, triage, classification) run well on a lighter model — cheaper and faster than your default.",
      thinking: "Reasoning",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      failureAlerts: "Failure alerts",
      failureAlertInherit: "Inherit global setting",
      failureAlertDisabled: "Disable for this job",
      failureAlertCustom: "Custom per-job settings",
      failureAlertsHelp: "Control when this job sends repeated-failure alerts.",
      failureAlertAfter: "Alert after",
      failureAlertAfterHelp: "Consecutive errors before alerting.",
      failureAlertCooldown: "Cooldown (seconds)",
      failureAlertCooldownHelp: "Minimum seconds between alerts.",
      failureAlertChannel: "Alert channel",
      failureAlertTo: "Alert to",
      failureAlertToPlaceholder: "+1555... or chat id",
      failureAlertToHelp: "Optional recipient override for failure alerts.",
      failureAlertMode: "Alert mode",
      failureAlertAnnounce: "Announce (via channel)",
      failureAlertWebhook: "Webhook (HTTP POST)",
      failureAlertAccountId: "Alert account ID",
      failureAlertAccountPlaceholder: "Account ID for multi-account setups",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the task if delivery itself fails.",
      cantAddYet: "Can't save yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      createTask: "Create automation",
      createAndRun: "Create & run now",
      cancel: "Cancel",
    },
    jobState: {
      next: "Next",
    },
    runEntry: {
      noSummary: "No summary.",
      deliverySuppression: "Delivery suppression: {reason}",
      runAt: "Run at",
      transcript: "Run transcript",
      viewTranscript: "View transcript",
      transcriptEmpty: "No messages in this run yet.",
      transcriptMissingMetadata: "This run is missing the identity needed to open its transcript.",
      transcriptUnavailable:
        "The exact run transcript is unavailable or ambiguous. Refresh run history and try again.",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      triggerIntervalTooShort:
        "Condition-triggered automations must run at least every 30 seconds.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      triggerScriptRequired: "Trigger script is required when the condition trigger is enabled.",
      triggerScriptPayloadUnsupported:
        "Script payloads cannot use condition triggers because both own the same saved state.",
      triggerScheduleUnsupported:
        "Condition triggers require an interval, cron, or stream schedule.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be 0 or greater (0 disables this timeout).",
      deliveryModeRequired:
        "Choose a delivery mode explicitly, or run openclaw doctor --fix to repair legacy settings.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must be a valid http(s):// URL without embedded credentials.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },

  linkReader: {
    loadingPreview: "Loading preview…",
    title: "Link reader",
    urlPlaceholder: "Supported link URL",
  },
};
